import { getApiPrefix } from '../utils/apiHelper';
import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateInput } from "../components/ui/date-input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { SortableHeader, sortData } from "../components/ui/sortable-header";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Upload, Search, Plus, Trash2, Euro, Loader2, FileText, AlertCircle, Trash, AlertTriangle, Layers, Calendar, Pencil, ChevronLeft, ChevronRight, Filter, X, Download, UserPlus, CalendarDays, CheckCircle2, Clock, CircleDashed, PieChart, ArrowLeftRight } from "lucide-react";
import { usePermissions } from "../hooks/usePermissions";
import { useFormValidation } from "../hooks/useFormValidation";
import { InputWithError } from "../components/common/InputWithError";
import { ExcelUploadHelper, EXCEL_COLUMNS } from "../components/ui/excel-upload-helper";
import { useTranslation } from "../contexts/I18nContext";

const ITEMS_PER_PAGE = 50;

export default function InvoicesPage() {
  const { t } = useTranslation();
  const { canEdit } = usePermissions();
  const canEditFatture = canEdit("fatture");
  
  const [invoices, setInvoices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterTipo, setFilterTipo] = useState("");
  const [filterStato, setFilterStato] = useState("");
  const [filterCompensate, setFilterCompensate] = useState(false);
  const [filterAllocazione, setFilterAllocazione] = useState("");
  const [appartamentiList, setAppartamentiList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingScadenze, setUploadingScadenze] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sort, setSort] = useState({ field: "data", direction: "desc" });
  
  // Inline filters state (column filters)
  const [showInlineFilters, setShowInlineFilters] = useState(false);
  const [inlineFilters, setInlineFilters] = useState({
    tipo_documento: "",
    numero: "",
    ragione_sociale: "",
    data_da: "",
    data_a: "",
    data_scadenza: "",
    modalita_pagamento: "",
    imponibile: "",
    imposta: "",
    totale: "",
    importo_pagato: "",
    stato_pagamento: "",
    stato_allocazione: "",
  });
  
  // Payment dialog
  const [paymentDialog, setPaymentDialog] = useState({ open: false, invoice: null });
  const [paymentForm, setPaymentForm] = useState({ importo: "", data_pagamento: "", note: "" });
  
  // Allocation dialog
  const [allocDialog, setAllocDialog] = useState({ open: false, invoice: null, allocations: [] });
  const [allocForm, setAllocForm] = useState({ 
    account_id: "", 
    cost_center_id: "", 
    importo: "", 
    iva_indeducibile: "",  // IVA indeducibile associata a questa riga
    tipo: "imponibile",
    data_inizio_competenza: "",
    data_fine_competenza: "",
    note: "",
    tipo_allocazione: "standard",
    genera_ammortamento: false,
    categoria_ammortamento_id: "",
    descrizione_bene: "",
    asset_id: "",
    automezzo_id: ""
  });
  const [editAllocId, setEditAllocId] = useState(null); // ID allocazione in modifica
  const [categorieImmobilizzazioni, setCategorieImmobilizzazioni] = useState([]);
  const [beniDisponibili, setBeniDisponibili] = useState({ assets: [], automezzi: [], cdc_non_allocabile: null });

  // NCA → Fattura di riferimento
  const [linkingNc, setLinkingNc] = useState(false);
  const [fattureAperte, setFattureAperte] = useState([]);
  const [selectedFatturaRif, setSelectedFatturaRif] = useState("");

  // Scadenza/Modalità dialog
  const [scadenzaDialog, setScadenzaDialog] = useState({ open: false, invoice: null });
  const [scadenzaForm, setScadenzaForm] = useState({ data_scadenza: "", modalita_pagamento: "" });
  const [scadenzeMultiple, setScadenzeMultiple] = useState([]);
  const [loadingScadenze, setLoadingScadenze] = useState(false);

  // New invoice dialog
  const [newInvoiceDialog, setNewInvoiceDialog] = useState(false);
  const [editInvoiceDialog, setEditInvoiceDialog] = useState({ open: false, invoice: null });
  const [newInvoiceForm, setNewInvoiceForm] = useState({
    tipo_documento: "FV",
    data: "",
    numero: "",
    ragione_sociale: "",
    imponibile: "",
    imposta: "",
    totale: "",
    data_scadenza: "",
    modalita_pagamento: ""
  });

  // Delete confirmation dialogs
  const [deleteAllDialog, setDeleteAllDialog] = useState(false);
  const [deleteOneDialog, setDeleteOneDialog] = useState({ open: false, id: null });

  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteMultipleDialog, setDeleteMultipleDialog] = useState(false);
  
  // Task assignment state
  const [users, setUsers] = useState([]);
  const [assignTaskDialog, setAssignTaskDialog] = useState({ open: false, invoice: null });
  const [assignTaskForm, setAssignTaskForm] = useState({ assigned_to: "", task_type: "appartamenti", importo: "", note: "" });
  
  // Loading states for various actions
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [savingAllocation, setSavingAllocation] = useState(false);
  const [savingScadenza, setSavingScadenza] = useState(false);
  const [deletingInvoice, setDeletingInvoice] = useState(null);
  const [deletingAllocation, setDeletingAllocation] = useState(null);
  const [assigningTask, setAssigningTask] = useState(false);

  // Validazione form nuova fattura
  const { errors: invoiceErrors, validate: validateInvoice, validateField: validateInvoiceField, clearErrors: clearInvoiceErrors } = useFormValidation({
    numero: { required: true },
    ragione_sociale: { required: true },
    data: { required: true }
  });

  const fetchData = useCallback(async () => {
    try {
      const prefix = getApiPrefix();
      // Non filtriamo automaticamente per sede - le fatture esistenti potrebbero non avere sede_id
      // Il filtro sede sarà implementato quando l'utente assegnerà le fatture alle sedi
      
      const [invRes, accRes, ccRes, pmRes, usersRes, catImmRes, beniRes, appRes] = await Promise.all([
        api.get(`${prefix}/invoices?page_size=5000`),
        api.get(`${prefix}/accounts`),
        api.get(`${prefix}/cost-centers`),
        api.get(`${prefix}/payment-methods`),
        api.get(`${prefix}/users`).catch(() => ({ data: [] })),
        api.get(`${prefix}/categorie-immobilizzazioni`).catch(() => ({ data: [] })),
        api.get(`${prefix}/ammortamenti/beni-disponibili`).catch(() => ({ data: { assets: [], automezzi: [], cdc_non_allocabile: null } })),
        api.get(`${prefix}/appartamenti`).catch(() => ({ data: [] })),
      ]);
      // Handle paginated response
      const invoiceData = invRes.data.items || invRes.data;
      setInvoices(invoiceData);
      setAccounts(accRes.data);
      setCostCenters(ccRes.data);
      setPaymentMethods(pmRes.data);
      setUsers(usersRes.data || []);
      setCategorieImmobilizzazioni(catImmRes.data || []);
      setBeniDisponibili(beniRes.data || { assets: [], automezzi: [], cdc_non_allocabile: null });
      setAppartamentiList(appRes.data || []);
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, []);

  // Ricarica quando cambia la sede
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Selection handlers - select only current page items
  const toggleSelectAll = () => {
    const currentPageIds = filteredInvoices
      .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
      .map(i => i.id);
    
    const allSelected = currentPageIds.every(id => selectedIds.has(id));
    
    if (allSelected) {
      const newSelected = new Set(selectedIds);
      currentPageIds.forEach(id => newSelected.delete(id));
      setSelectedIds(newSelected);
    } else {
      const newSelected = new Set(selectedIds);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedIds(newSelected);
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const [deletingBulk, setDeletingBulk] = useState(false);

  const handleDeleteMultiple = async () => {
    const ids = Array.from(selectedIds);
    setDeletingBulk(true);
    try {
      const res = await api.post(`${getApiPrefix()}/invoices/bulk-delete`, { ids });
      showSuccess(res.data.message || `${ids.length} fatture eliminate`);
      setSelectedIds(new Set());
      setDeleteMultipleDialog(false);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorMultiDelete"));
    } finally {
      setDeletingBulk(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await api.post(`${getApiPrefix()}/invoices/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000, // 5 minuti per file grandi
      });
      showSuccess(res.data.message);
      fetchData();
    } catch (err) {
      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        handleApiError({ message: "Il caricamento sta impiegando troppo tempo. Attendi qualche istante e ricarica la pagina per verificare se i dati sono stati importati. Non riprovare il caricamento per evitare duplicati." });
      } else {
        handleApiError(err, t("errors.errorUpload"));
      }
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleUploadScadenze = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingScadenze(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await api.post(`${getApiPrefix()}/invoices/upload-scadenze`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      showSuccess(res.data.message);
      fetchData();
    } catch (err) {
      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        handleApiError({ message: "Il caricamento sta impiegando troppo tempo. Attendi qualche istante e ricarica la pagina per verificare se i dati sono stati importati. Non riprovare il caricamento per evitare duplicati." });
      } else {
        handleApiError(err, t("errors.errorUploadDeadlines"));
      }
    } finally {
      setUploadingScadenze(false);
      e.target.value = "";
    }
  };

  const handleCreateInvoice = async () => {
    // Validazione client-side
    if (!validateInvoice(newInvoiceForm)) {
      return;
    }
    setSavingInvoice(true);
    try {
      const payload = {
        tipo_documento: newInvoiceForm.tipo_documento,
        data: newInvoiceForm.data,
        numero: newInvoiceForm.numero,
        fornitore_cliente: newInvoiceForm.ragione_sociale,
        imponibile: parseFloat(newInvoiceForm.imponibile) || 0,
        iva: parseFloat(newInvoiceForm.imposta) || 0,
        totale: parseFloat(newInvoiceForm.totale) || 0,
        data_scadenza: newInvoiceForm.data_scadenza || null,
        modalita_pagamento_id: newInvoiceForm.modalita_pagamento || null
      };
      await api.post(`${getApiPrefix()}/invoices`, payload);
      showSuccess(t("success.invoiceCreated"));
      setNewInvoiceDialog(false);
      setNewInvoiceForm({
        tipo_documento: "FV",
        data: "",
        numero: "",
        ragione_sociale: "",
        imponibile: "",
        imposta: "",
        totale: "",
        data_scadenza: "",
        modalita_pagamento: ""
      });
      clearInvoiceErrors();
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      let errorMsg = "Errore creazione fattura";
      if (detail) {
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(d => d.msg || JSON.stringify(d)).join(', ');
        } else if (typeof detail === 'object' && detail.msg) {
          errorMsg = detail.msg;
        }
      }
      handleApiError({ message: errorMsg });
    } finally {
      setSavingInvoice(false);
    }
  };

  // Auto-calculate totale when imponibile or imposta change
  const updateNewInvoiceForm = (field, value) => {
    const updated = { ...newInvoiceForm, [field]: value };
    if (field === "imponibile" || field === "imposta") {
      const imponibile = parseFloat(updated.imponibile) || 0;
      const imposta = parseFloat(updated.imposta) || 0;
      updated.totale = String((imponibile + imposta).toFixed(2));
    }
    setNewInvoiceForm(updated);
  };

  const openEditInvoice = (inv) => {
    setNewInvoiceForm({
      tipo_documento: inv.tipo_documento || "FA",
      data: inv.data || "",
      numero: inv.numero || "",
      ragione_sociale: inv.fornitore_cliente || "",
      imponibile: String(inv.imponibile || ""),
      imposta: String(inv.iva || ""),
      totale: String(inv.totale || ""),
      data_scadenza: inv.data_scadenza || "",
      modalita_pagamento: inv.modalita_pagamento_id || ""
    });
    setEditInvoiceDialog({ open: true, invoice: inv });
  };

  const handleUpdateInvoice = async () => {
    if (!newInvoiceForm.numero || !newInvoiceForm.ragione_sociale || !newInvoiceForm.data) {
      handleApiError({ message: t("errors.requiredInvoiceFields") });
      return;
    }
    setSavingInvoice(true);
    try {
      const payload = {
        tipo_documento: newInvoiceForm.tipo_documento,
        data: newInvoiceForm.data,
        numero: newInvoiceForm.numero,
        fornitore_cliente: newInvoiceForm.ragione_sociale,
        imponibile: parseFloat(newInvoiceForm.imponibile) || 0,
        iva: parseFloat(newInvoiceForm.imposta) || 0,
        totale: parseFloat(newInvoiceForm.totale) || 0,
        data_scadenza: newInvoiceForm.data_scadenza || null,
        modalita_pagamento_id: newInvoiceForm.modalita_pagamento || null
      };
      await api.put(`${getApiPrefix()}/invoices/${editInvoiceDialog.invoice.id}`, payload);
      showSuccess(t("success.invoiceUpdated"));
      setEditInvoiceDialog({ open: false, invoice: null });
      setNewInvoiceForm({ tipo_documento: "FV", data: "", numero: "", ragione_sociale: "", imponibile: "", imposta: "", totale: "", data_scadenza: "", modalita_pagamento: "" });
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      let errorMsg = "Errore aggiornamento fattura";
      if (detail) {
        if (typeof detail === 'string') errorMsg = detail;
        else if (Array.isArray(detail)) errorMsg = detail.map(d => d.msg || JSON.stringify(d)).join(', ');
      }
      handleApiError({ message: errorMsg });
    } finally {
      setSavingInvoice(false);
    }
  };


  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState("");

  const handleDeleteAll = async () => {
    // Ricava lo slug del tenant dall'URL: /:tenant_slug/...
    const tenantSlug = (window.location.pathname.split('/').filter(Boolean)[0] || '').toLowerCase();
    if (!tenantSlug) {
      handleApiError({ message: "Impossibile determinare lo slug del tenant" });
      return;
    }
    if ((deleteAllConfirmText || "").trim().toLowerCase() !== tenantSlug) {
      handleApiError({ message: `Devi digitare esattamente '${tenantSlug}' per confermare` });
      return;
    }
    setDeletingInvoice('all');
    try {
      const res = await api.delete(`${getApiPrefix()}/invoices`, { params: { conferma_tenant: tenantSlug } });
      showSuccess(res.data.message);
      setDeleteAllDialog(false);
      setDeleteAllConfirmText("");
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    } finally {
      setDeletingInvoice(null);
    }
  };

  const handleDelete = async (id) => {
    setDeletingInvoice(id);
    try {
      await api.delete(`${getApiPrefix()}/invoices/${id}`);
      showSuccess(t("success.invoiceDeleted"));
      setDeleteOneDialog({ open: false, id: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    } finally {
      setDeletingInvoice(null);
    }
  };

  const handlePayment = async () => {
    if (!paymentForm.importo || !paymentForm.data_pagamento) {
      handleApiError({ message: t("errors.requiredFields") });
      return;
    }
    setSavingPayment(true);
    try {
      await api.post(`${getApiPrefix()}/invoices/${paymentDialog.invoice.id}/payments`, {
        importo: parseFloat(paymentForm.importo),
        data_pagamento: paymentForm.data_pagamento,
        note: paymentForm.note || null,
      });
      showSuccess(t("success.paymentRegistered"));
      setPaymentDialog({ open: false, invoice: null });
      setPaymentForm({ importo: "", data_pagamento: "", note: "" });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorRegisterPayment"));
    } finally {
      setSavingPayment(false);
    }
  };

  // Allocation functions
  const openAllocDialog = async (invoice) => {
    try {
      const res = await api.get(`${getApiPrefix()}/invoices/${invoice.id}/allocations`);
      setAllocDialog({ open: true, invoice, allocations: res.data });
      // Setup stato NCA se necessario
      setSelectedFatturaRif(invoice.invoice_id_riferimento || "");
      setFattureAperte([]);
      const tipoDoc = invoice.tipo_documento;
      if ((tipoDoc === 'NCA' || tipoDoc === 'NCAE') && invoice.fornitore_cliente) {
        api.get(`${getApiPrefix()}/invoices/fatture-passive-aperte`, {
          params: { fornitore: invoice.fornitore_cliente, exclude_id: invoice.id }
        }).then(r => setFattureAperte(r.data || [])).catch(() => {});
      }
      // Ricarica in parallelo tutte le anagrafiche utilizzate nei dropdown del dialog,
      // così l'utente vede subito asset/categorie/conti/CDC appena creati senza dover
      // ricaricare la pagina fatture.
      const prefix = getApiPrefix();
      Promise.all([
        api.get(`${prefix}/ammortamenti/beni-disponibili`, { params: { exclude_fattura_id: invoice.id } }).catch(() => null),
        api.get(`${prefix}/categorie-immobilizzazioni`).catch(() => null),
        api.get(`${prefix}/accounts`).catch(() => null),
        api.get(`${prefix}/cost-centers`).catch(() => null),
        api.get(`${prefix}/appartamenti`).catch(() => null),
      ]).then(([beniRes, catRes, accRes, ccRes, appRes]) => {
        if (beniRes?.data) setBeniDisponibili(beniRes.data || { assets: [], automezzi: [], cdc_non_allocabile: null });
        if (catRes?.data) setCategorieImmobilizzazioni(catRes.data || []);
        if (accRes?.data) setAccounts(accRes.data || []);
        if (ccRes?.data) setCostCenters(ccRes.data || []);
        if (appRes?.data) setAppartamentiList(appRes.data || []);
      });
      const imponibile = Math.abs(invoice.imponibile || 0);
      const imponibileAllocato = invoice.imponibile_allocato || 0;
      // Default date competence from invoice date
      const dataCompetenza = invoice.data || "";
      setAllocForm({ 
        account_id: "", 
        cost_center_id: "", 
        importo: String((imponibile - imponibileAllocato).toFixed(2)), 
        tipo: "imponibile",
        data_inizio_competenza: dataCompetenza,
        data_fine_competenza: dataCompetenza,
        note: "" 
      });
    } catch (err) {
      handleApiError(err, t("errors.loadAllocations"));
    }
  };

  // Refresh allocations + invoice after NC compensation
  const refreshAllocDialog = async () => {
    if (!allocDialog.invoice?.id) return;
    try {
      const prefix = getApiPrefix();
      const [allocRes, invRes] = await Promise.all([
        api.get(`${prefix}/invoices/${allocDialog.invoice.id}/allocations`),
        api.get(`${prefix}/invoices/${allocDialog.invoice.id}`),
      ]);
      setAllocDialog(prev => ({ ...prev, invoice: invRes.data, allocations: allocRes.data }));
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error("Errore refresh", err);
    }
  };

  const handleLinkNc = async () => {
    if (!selectedFatturaRif || !allocDialog.invoice) return;
    setLinkingNc(true);
    try {
      const prefix = getApiPrefix();
      const res = await api.post(`${prefix}/invoices/${allocDialog.invoice.id}/link-to-fattura`, {
        invoice_id_riferimento: selectedFatturaRif,
      });
      showSuccess(`Nota di credito associata: chiusura ${res.data?.chiusura || ''}`);
      await refreshAllocDialog();
      await fetchData();
    } catch (err) {
      handleApiError(err, "Errore associazione nota di credito");
    } finally {
      setLinkingNc(false);
    }
  };

  const handleUnlinkNc = async () => {
    if (!allocDialog.invoice?.id) return;
    if (!window.confirm(t("common.confirmRemoveNcLink") || "Rimuovere l'associazione della nota di credito alla fattura? Gli stati verranno ripristinati.")) return;
    setLinkingNc(true);
    try {
      const prefix = getApiPrefix();
      await api.post(`${prefix}/invoices/${allocDialog.invoice.id}/unlink-from-fattura`);
      showSuccess("Associazione rimossa");
      setSelectedFatturaRif("");
      // Ricarica fatture aperte (potrebbe essere apparsa la vecchia)
      const inv = allocDialog.invoice;
      if (inv?.fornitore_cliente) {
        api.get(`${getApiPrefix()}/invoices/fatture-passive-aperte`, {
          params: { fornitore: inv.fornitore_cliente, exclude_id: inv.id }
        }).then(r => setFattureAperte(r.data || [])).catch(() => {});
      }
      await refreshAllocDialog();
      await fetchData();
    } catch (err) {
      handleApiError(err, "Errore rimozione associazione");
    } finally {
      setLinkingNc(false);
    }
  };

  const handleAddAllocation = async () => {
    const isBollettaMode = ['bollette', 'manutenzioni_appartamenti'].includes(allocForm.tipo_allocazione);
    
    if (!isBollettaMode && !allocForm.account_id || !allocForm.importo) {
      if (!isBollettaMode && !allocForm.account_id) {
        handleApiError({ message: t("errors.requiredAmountSelect") });
        return;
      }
      if (!allocForm.importo) {
        handleApiError({ message: t("errors.requiredAmountEnter") });
        return;
      }
    }
    
    // Validazione ammortamento
    if (allocForm.genera_ammortamento) {
      if (!allocForm.categoria_ammortamento_id) {
        handleApiError({ message: t("errors.requiredSelectCategory") });
        return;
      }
      if (!allocForm.descrizione_bene) {
        handleApiError({ message: t("errors.requiredAssetDescription") });
        return;
      }
      // Verifica che sia selezionato un asset o automezzo
      if (!allocForm.asset_id && !allocForm.automezzo_id) {
        handleApiError({ message: t("errors.requiredSelectAssetOrVehicle") });
        return;
      }
    }
    
    try {
      let res;
      
      // Se ammortamento con bene selezionato, usa la data fattura come data competenza
      const dataCompetenza = (allocForm.genera_ammortamento && (allocForm.asset_id || allocForm.automezzo_id))
        ? allocDialog.invoice?.data
        : isBollettaMode ? null : allocForm.data_inizio_competenza;
      
      const allocData = {
        account_id: isBollettaMode ? null : allocForm.account_id,
        cost_center_id: isBollettaMode ? null : (allocForm.cost_center_id || null),
        importo: parseFloat(allocForm.importo),
        iva_indeducibile: isBollettaMode ? null : (allocForm.iva_indeducibile ? parseFloat(allocForm.iva_indeducibile) : null),
        tipo: allocForm.tipo,
        data_inizio_competenza: dataCompetenza || null,
        data_fine_competenza: isBollettaMode ? null : (allocForm.data_fine_competenza || null),
        note: isBollettaMode ? null : (allocForm.note || null),
        tipo_allocazione: allocForm.tipo_allocazione || "standard"
      };
      
      if (editAllocId) {
        // Modifica allocazione esistente
        res = await api.put(`${getApiPrefix()}/invoices/${allocDialog.invoice.id}/allocations/${editAllocId}`, allocData);
        showSuccess(t("success.allocationUpdated"));
        setEditAllocId(null);
      } else {
        // Nuova allocazione
        res = await api.post(`${getApiPrefix()}/invoices/${allocDialog.invoice.id}/allocations`, allocData);
        
        // Se genera ammortamento, crealo
        if (allocForm.genera_ammortamento && res.data.allocation_id) {
          const ammParams = new URLSearchParams({
            allocation_id: res.data.allocation_id,
            fattura_id: allocDialog.invoice.id,
            categoria_id: allocForm.categoria_ammortamento_id,
            descrizione_bene: allocForm.descrizione_bene,
            valore_bene: String(parseFloat(allocForm.importo)),
            data_inizio: allocDialog.invoice.data || new Date().toISOString().split('T')[0]
          });
          if (allocForm.note) ammParams.append("note", allocForm.note);
          if (allocForm.asset_id) ammParams.append("asset_id", allocForm.asset_id);
          if (allocForm.automezzo_id) ammParams.append("automezzo_id", allocForm.automezzo_id);
          
          await api.post(`${getApiPrefix()}/ammortamenti?${ammParams.toString()}`);
          showSuccess(t("success.allocationAndAmortization"));
        } else if (isBollettaMode) {
          showSuccess(t("success.allocationWithBills"));
        } else {
          showSuccess(t("success.allocationAdded"));
        }
      }
      // Refresh allocations and update invoice in dialog
      const allocRes = await api.get(`${getApiPrefix()}/invoices/${allocDialog.invoice.id}/allocations`);
      const updatedInvoice = res.data.invoice;
      setAllocDialog({ ...allocDialog, invoice: updatedInvoice, allocations: allocRes.data });
      // Set new residuo based on tipo
      const residuo = allocForm.tipo === "iva_indeducibile" 
        ? res.data.residuo_iva 
        : res.data.residuo_imponibile;
      setAllocForm({ 
        ...allocForm,
        account_id: "", 
        cost_center_id: "", 
        importo: String(Math.max(0, residuo).toFixed(2)), 
        iva_indeducibile: "",
        note: "",
        tipo_allocazione: "standard",
        genera_ammortamento: false,
        categoria_ammortamento_id: "",
        descrizione_bene: "",
        asset_id: "",
        automezzo_id: ""
      });
      // Update local invoices state immediately for reactivity
      setInvoices(prev => prev.map(inv => 
        inv.id === updatedInvoice.id ? updatedInvoice : inv
      ));
    } catch (err) {
      handleApiError(err, t("errors.errorAllocation"));
    }
  };

  const handleEditAllocation = (alloc) => {
    // Popola il form con i dati dell'allocazione da modificare
    const tipo = (alloc.descrizione || "").includes("iva_indeducibile") ? "iva_indeducibile" : "imponibile";
    setAllocForm({
      account_id: alloc.conto_id || "",
      cost_center_id: alloc.centro_costo_id || "",
      importo: String(alloc.importo || ""),
      iva_indeducibile: String(alloc.iva_indeducibile || ""),
      tipo: tipo,
      data_inizio_competenza: alloc.data_inizio_competenza || "",
      data_fine_competenza: alloc.data_fine_competenza || "",
      note: (alloc.descrizione || "").replace("imponibile", "").replace("iva_indeducibile", "").replace(" - ", "").trim()
    });
    setEditAllocId(alloc.id);
  };

  const handleCancelEdit = () => {
    setEditAllocId(null);
    // Reset form al residuo corrente
    const residuoImponibile = Math.abs(allocDialog.invoice?.imponibile || 0) - (allocDialog.invoice?.imponibile_allocato || 0);
    setAllocForm({
      account_id: "",
      cost_center_id: "",
      importo: String(Math.max(0, residuoImponibile).toFixed(2)),
      iva_indeducibile: "",
      tipo: "imponibile",
      data_inizio_competenza: "",
      data_fine_competenza: "",
      note: ""
    });
  };

  const handleDeleteAllocation = async (allocId) => {
    try {
      await api.delete(`${getApiPrefix()}/invoices/${allocDialog.invoice.id}/allocations/${allocId}`);
      showSuccess(t("success.allocationDeleted"));
      // Refresh allocations and invoice
      const allocRes = await api.get(`${getApiPrefix()}/invoices/${allocDialog.invoice.id}/allocations`);
      const invRes = await api.get(`${getApiPrefix()}/invoices/${allocDialog.invoice.id}`);
      setAllocDialog({ ...allocDialog, invoice: invRes.data, allocations: allocRes.data });
      // Aggiorna il form con il nuovo residuo
      const residuoImponibile = Math.abs(invRes.data.imponibile || 0) - (invRes.data.imponibile_allocato || 0);
      setAllocForm(prev => ({
        ...prev,
        importo: String(Math.max(0, residuoImponibile).toFixed(2))
      }));
      // Update local invoices state immediately for reactivity
      setInvoices(prev => prev.map(inv => 
        inv.id === invRes.data.id ? invRes.data : inv
      ));
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  // Scadenza/Modalità functions
  const openScadenzaDialog = async (invoice) => {
    setScadenzaDialog({ open: true, invoice });
    setScadenzaForm({
      data_scadenza: invoice.data_scadenza || invoice.data || "",
      modalita_pagamento: invoice.modalita_pagamento || invoice.modalita_pagamento_id || ""
    });
    setEditingRataId(null);
    // Carica scadenze multiple
    setLoadingScadenze(true);
    try {
      const res = await api.get(`${getApiPrefix()}/invoices/${invoice.id}/scadenze`);
      setScadenzeMultiple(res.data || []);
    } catch (err) {
      setScadenzeMultiple([]);
    } finally {
      setLoadingScadenze(false);
    }
  };

  const handleUpdateScadenza = async () => {
    try {
      const res = await api.patch(`${getApiPrefix()}/invoices/${scadenzaDialog.invoice.id}/scadenza`, scadenzaForm);
      const propagated = res?.data?.propagated_to_supplier_invoices || 0;
      if (propagated > 0) {
        showSuccess(`Scadenza e modalità aggiornate. Modalità propagata a ${propagated} altre fatture di ${res.data.fornitore_cliente}.`);
      } else {
        showSuccess(t("success.deadlineAndMethodUpdated"));
      }
      setScadenzaDialog({ open: false, invoice: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.updateFailed"));
    }
  };

  const [nuovaRata, setNuovaRata] = useState({ data_scadenza: "", importo: "", note: "" });
  const [editingRataId, setEditingRataId] = useState(null);
  const [editRataForm, setEditRataForm] = useState({ data_scadenza: "", importo: "", note: "" });

  const reloadScadenze = async () => {
    const res = await api.get(`${getApiPrefix()}/invoices/${scadenzaDialog.invoice.id}/scadenze`);
    setScadenzeMultiple(res.data || []);
  };

  const handleAddRata = async () => {
    if (!nuovaRata.data_scadenza || !nuovaRata.importo) return;
    try {
      await api.post(`${getApiPrefix()}/invoices/${scadenzaDialog.invoice.id}/scadenze`, {
        data_scadenza: nuovaRata.data_scadenza,
        importo: parseFloat(nuovaRata.importo),
        note: nuovaRata.note || null
      });
      showSuccess(t("success.rateAdded"));
      setNuovaRata({ data_scadenza: "", importo: "", note: "" });
      await reloadScadenze();
    } catch (err) {
      handleApiError(err, t("errors.errorAddRate"));
    }
  };

  const handleUpdateRata = async (rataId) => {
    if (!editRataForm.data_scadenza || !editRataForm.importo) return;
    try {
      await api.put(`${getApiPrefix()}/invoices/${scadenzaDialog.invoice.id}/scadenze/${rataId}`, {
        data_scadenza: editRataForm.data_scadenza,
        importo: parseFloat(editRataForm.importo),
        note: editRataForm.note || null
      });
      showSuccess(t("invoices.dueDateUpdated"));
      setEditingRataId(null);
      await reloadScadenze();
    } catch (err) {
      handleApiError(err, "Errore aggiornamento scadenza");
    }
  };

  const handleDeleteRata = async (rataId) => {
    try {
      await api.delete(`${getApiPrefix()}/invoices/${scadenzaDialog.invoice.id}/scadenze/${rataId}`);
      showSuccess(t("success.rateDeleted"));
      await reloadScadenze();
    } catch (err) {
      handleApiError(err, t("errors.errorDeleteRate"));
    }
  };

  // Task Assignment functions
  const openAssignTaskDialog = (invoice) => {
    setAssignTaskDialog({ open: true, invoice });
    const residuo = Math.abs(invoice.imponibile || 0) - (invoice.imponibile_allocato || 0);
    setAssignTaskForm({
      assigned_to: "",
      task_type: "appartamenti",
      importo: String(residuo.toFixed(2)),
      note: ""
    });
  };

  const handleAssignTask = async () => {
    if (!assignTaskForm.assigned_to) {
      handleApiError({ message: t("errors.requiredSelectUser") });
      return;
    }
    if (!assignTaskForm.importo || parseFloat(assignTaskForm.importo) <= 0) {
      handleApiError({ message: t("errors.requiredValidAmountEnter") });
      return;
    }
    setAssigningTask(true);
    try {
      await api.post(`${getApiPrefix()}/tasks`, {
        assigned_to: assignTaskForm.assigned_to,
        task_type: assignTaskForm.task_type,
        invoice_id: assignTaskDialog.invoice.id,
        importo: parseFloat(assignTaskForm.importo),
        descrizione: `Fattura ${assignTaskDialog.invoice.numero} - ${assignTaskDialog.invoice.ragione_sociale || assignTaskDialog.invoice.fornitore_cliente}`,
        note: assignTaskForm.note || null
      });
      showSuccess(t("success.taskAssigned"));
      setAssignTaskDialog({ open: false, invoice: null });
      setAssignTaskForm({ assigned_to: "", task_type: "appartamenti", importo: "", note: "" });
    } catch (err) {
      handleApiError(err, t("errors.errorAssignTask"));
    } finally {
      setAssigningTask(false);
    }
  };

  // Download template Excel
  const handleDownloadTemplate = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${backendUrl}/api/templates/MODELLO_FATTURE.xlsx`, '_blank');
  };

  const formatCurrency = (val) =>
    new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(val);

  const getStatusBadge = (stato) => {
    const variants = {
      pagata: "default",
      parziale: "secondary",
      non_pagata: "destructive",
    };
    const labels = {
      pagata: "Pagata",
      parziale: "Parziale",
      non_pagata: "Non pagata",
    };
    return <Badge variant={variants[stato] || "outline"} className={`status-${stato}`}>{labels[stato] || stato}</Badge>;
  };

  const getAllocStatusBadge = (stato) => {
    const config = {
      non_allocata: { className: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400", label: "Da allocare" },
      parziale: { className: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400", label: "Parziale" },
      in_allocazione: { className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400", label: "In Allocazione" },
      allocata: { className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400", label: "Allocata" },
    };
    const c = config[stato] || { className: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400", label: "Da allocare" };
    return <Badge variant="outline" className={c.className}>{c.label}</Badge>;
  };

  const getAccountName = (id) => accounts.find(a => a.id === id)?.nome || "";
  const getCostCenterName = (id) => costCenters.find(c => c.id === id)?.nome || "";

  // Clear inline filters
  const clearInlineFilters = () => {
    setInlineFilters({
      tipo_documento: "",
      numero: "",
      ragione_sociale: "",
      data_da: "",
      data_a: "",
      data_scadenza: "",
      modalita_pagamento: "",
      imponibile: "",
      imposta: "",
      totale: "",
      importo_pagato: "",
      stato_pagamento: "",
      stato_allocazione: "",
    });
  };
  const hasActiveInlineFilters = Object.values(inlineFilters).some(v => v);

  // Parse DD-MM-YYYY into a comparable YYYYMMDD numeric string; returns null on invalid/partial input
  const parseDateForCompare = (s) => {
    if (!s) return null;
    const parts = s.split(/[-/]/);
    if (parts.length !== 3) return null;
    const [d, m, y] = parts;
    if (!d || !m || !y || y.length !== 4) return null;
    return `${y}${String(m).padStart(2, '0')}${String(d).padStart(2, '0')}`;
  };

  const filteredInvoices = invoices.filter((inv) => {
    // Handle both ragione_sociale and fornitore_cliente field names
    const ragioneSociale = inv.ragione_sociale || inv.fornitore_cliente || "";
    const numero = inv.numero || "";
    const matchSearch =
      ragioneSociale.toLowerCase().includes(search.toLowerCase()) ||
      numero.toLowerCase().includes(search.toLowerCase());
    const matchTipo = !filterTipo || filterTipo === "all" || inv.tipo_documento === filterTipo;
    const matchStato = !filterStato || filterStato === "all" || inv.stato_pagamento === filterStato;
    const matchAlloc = !filterAllocazione || filterAllocazione === "all" || (inv.stato_allocazione || "non_allocata") === filterAllocazione;
    const matchCompens = !filterCompensate || inv.compensata === true;
    
    // Inline column filters
    const numMatch = (val, filter) => {
      if (!filter) return true;
      return String(val ?? "").toLowerCase().includes(String(filter).toLowerCase());
    };
    const matchNumero = !inlineFilters.numero || numero.toLowerCase().includes(inlineFilters.numero.toLowerCase());
    const matchRagSoc = !inlineFilters.ragione_sociale || ragioneSociale.toLowerCase().includes(inlineFilters.ragione_sociale.toLowerCase());
    // Data range filter (Dal ... Al ...). Solo se completa DD-MM-YYYY.
    let matchData = true;
    const invDataKey = parseDateForCompare(inv.data);
    const dal = parseDateForCompare(inlineFilters.data_da);
    const al = parseDateForCompare(inlineFilters.data_a);
    if (dal) {
      if (!invDataKey || invDataKey < dal) matchData = false;
    }
    if (matchData && al) {
      if (!invDataKey || invDataKey > al) matchData = false;
    }
    const matchScadenza = !inlineFilters.data_scadenza || (inv.data_scadenza || "").includes(inlineFilters.data_scadenza);
    const matchModPag = !inlineFilters.modalita_pagamento || (inv.modalita_pagamento || "").toLowerCase().includes(inlineFilters.modalita_pagamento.toLowerCase());
    const matchTipoInline = !inlineFilters.tipo_documento || inlineFilters.tipo_documento === "all" || inv.tipo_documento === inlineFilters.tipo_documento;
    const matchImponibile = numMatch(inv.imponibile, inlineFilters.imponibile);
    const matchImposta = numMatch(inv.imposta, inlineFilters.imposta);
    const matchTotale = numMatch(inv.totale, inlineFilters.totale);
    const matchImportoPagato = numMatch(inv.importo_pagato, inlineFilters.importo_pagato);
    const matchStatoInline = !inlineFilters.stato_pagamento || inlineFilters.stato_pagamento === "all" || inv.stato_pagamento === inlineFilters.stato_pagamento;
    const matchAllocInline = !inlineFilters.stato_allocazione || inlineFilters.stato_allocazione === "all" || (inv.stato_allocazione || "non_allocata") === inlineFilters.stato_allocazione;
    
    return matchSearch && matchTipo && matchStato && matchAlloc && matchCompens &&
           matchNumero && matchRagSoc && matchData && matchScadenza && matchModPag &&
           matchTipoInline && matchImponibile && matchImposta && matchTotale && matchImportoPagato &&
           matchStatoInline && matchAllocInline;
  });

  // Sort and Paginate
  const sortedInvoices = sortData(filteredInvoices, sort);
  const totalPages = Math.ceil(sortedInvoices.length / ITEMS_PER_PAGE);
  const paginatedInvoices = sortedInvoices.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterTipo, filterStato, filterAllocazione, filterCompensate]);

  // Calculate stats
  const totaleImponibile = invoices.reduce((s, i) => s + Math.abs(i.imponibile || 0), 0);
  const totaleImposta = invoices.reduce((s, i) => s + Math.abs(i.iva || 0), 0);
  const totaleFatture = invoices.reduce((s, i) => s + Math.abs(i.totale || 0), 0);
  const totalePagato = invoices.reduce((s, i) => s + (i.importo_pagato || 0), 0);
  const fattureNonPagate = invoices.filter((i) => i.stato_pagamento === "non_pagata").length;

  // Statistiche per stato di pagamento e allocazione (count + totale €)
  const statPagamento = {
    pagata: { count: 0, totale: 0 },
    parziale: { count: 0, totale: 0 },
    non_pagata: { count: 0, totale: 0 },
  };
  const statAllocazione = {
    allocata: { count: 0, totale: 0 },
    parziale: { count: 0, totale: 0 },
    in_allocazione: { count: 0, totale: 0 },
    non_allocata: { count: 0, totale: 0 },
  };
  invoices.forEach((i) => {
    const tot = Math.abs(i.totale || 0);
    const stPag = i.stato_pagamento || "non_pagata";
    if (statPagamento[stPag]) {
      statPagamento[stPag].count += 1;
      statPagamento[stPag].totale += tot;
    }
    const stAlloc = i.stato_allocazione || "non_allocata";
    if (statAllocazione[stAlloc]) {
      statAllocazione[stAlloc].count += 1;
      statAllocazione[stAlloc].totale += tot;
    }
  });

  // Click handler per filtrare dalle card
  const applyFilterPagamento = (s) => {
    setFilterStato(s === filterStato ? "all" : s);
  };
  const applyFilterAllocazione = (s) => {
    setFilterAllocazione(s === filterAllocazione ? "all" : s);
  };

  // Check for anomalies (paid > total)
  const hasAnomaly = (inv) => inv.importo_pagato > Math.abs(inv.totale) + 0.01;

  // Selection helpers - based on current page
  const isAllSelected = paginatedInvoices.length > 0 && paginatedInvoices.every(inv => selectedIds.has(inv.id));
  const isSomeSelected = selectedIds.size > 0 && !isAllSelected;

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
  }

  const currentInvoice = allocDialog.invoice;
  const residuoImponibile = currentInvoice ? Math.abs(currentInvoice.imponibile || 0) - (currentInvoice.imponibile_allocato || 0) : 0;
  const residuoIva = currentInvoice ? Math.abs(currentInvoice.iva || 0) - (currentInvoice.iva_allocata || 0) : 0;
  const isBollettaTipo = ['bollette', 'manutenzioni_appartamenti'].includes(allocForm.tipo_allocazione);
  const residuoTotale = residuoImponibile + residuoIva;
  // Permetti salvataggio se in modifica OPPURE se c'è residuo per nuova allocazione
  const canAddAllocation = editAllocId 
    ? (allocForm.account_id && allocForm.importo)
    : isBollettaTipo
      ? residuoTotale > 0.01
      : (allocForm.tipo === "iva_indeducibile" ? residuoIva > 0.01 : residuoImponibile > 0.01);

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("invoices.title")}</h1>
          <p className="text-muted-foreground text-sm">Gestione fatture attive e passive</p>
        </div>
        <Badge variant="outline" className="text-sm px-3 py-1 font-mono">
          {invoices.length} documenti
        </Badge>
      </div>

      {/* Stats Cards - Improved Design */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card className="border-l-4 border-l-indigo-500 bg-gradient-to-r from-indigo-50/50 to-transparent dark:from-indigo-950/20">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">{t('invoices.title')}</p>
                <p className="text-3xl font-bold font-mono mt-1">{invoices.length}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                <FileText className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('invoices.taxableAmount')}</p>
            <p className="text-2xl font-bold font-mono tabular-nums mt-1">{formatCurrency(totaleImponibile)}</p>
            <p className="text-xs text-muted-foreground mt-1">Base imponibile totale</p>
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">IVA</p>
            <p className="text-2xl font-bold font-mono tabular-nums mt-1">{formatCurrency(totaleImposta)}</p>
            <p className="text-xs text-muted-foreground mt-1">Imposta totale</p>
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("common.total")}</p>
            <p className="text-2xl font-bold font-mono tabular-nums mt-1">{formatCurrency(totaleFatture)}</p>
            <p className="text-xs text-muted-foreground mt-1">Importo complessivo</p>
          </CardContent>
        </Card>
        
        <Card className={`border-l-4 ${fattureNonPagate > 0 ? 'border-l-red-500 bg-gradient-to-r from-red-50/50 to-transparent dark:from-red-950/20' : 'border-l-emerald-500 bg-gradient-to-r from-emerald-50/50 to-transparent dark:from-emerald-950/20'}`}>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs font-semibold uppercase tracking-wider ${fattureNonPagate > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{t('invoices.notPaidPlural')}</p>
                <p className={`text-3xl font-bold font-mono mt-1 ${fattureNonPagate > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                  {fattureNonPagate}
                </p>
              </div>
              <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${fattureNonPagate > 0 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-emerald-100 dark:bg-emerald-900/30'}`}>
                <AlertCircle className={`h-6 w-6 ${fattureNonPagate > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dashboard: Stati di Pagamento (click to filter) */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
          <Euro className="h-3.5 w-3.5" />{t('invoices.paymentStatus')}</p>
        <div className="grid gap-3 md:grid-cols-3">
          <Card
            className={`cursor-pointer hover:shadow-md transition border-l-4 border-l-emerald-500 ${filterStato === "pagata" ? "ring-2 ring-emerald-400 bg-emerald-50/60 dark:bg-emerald-950/30" : ""}`}
            onClick={() => applyFilterPagamento("pagata")}
            data-testid="stat-pag-pagata"
          >
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider flex items-center gap-1">
                    <CheckCircle2 className="h-3.5 w-3.5" />{t('invoices.paidPlural')}</p>
                  <p className="text-2xl font-bold font-mono mt-1">{statPagamento.pagata.count}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 font-mono">{formatCurrency(statPagamento.pagata.totale)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer hover:shadow-md transition border-l-4 border-l-amber-500 ${filterStato === "parziale" ? "ring-2 ring-amber-400 bg-amber-50/60 dark:bg-amber-950/30" : ""}`}
            onClick={() => applyFilterPagamento("parziale")}
            data-testid="stat-pag-parziale"
          >
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" /> Parziali
                  </p>
                  <p className="text-2xl font-bold font-mono mt-1">{statPagamento.parziale.count}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 font-mono">{formatCurrency(statPagamento.parziale.totale)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer hover:shadow-md transition border-l-4 border-l-red-500 ${filterStato === "non_pagata" ? "ring-2 ring-red-400 bg-red-50/60 dark:bg-red-950/30" : ""}`}
            onClick={() => applyFilterPagamento("non_pagata")}
            data-testid="stat-pag-non-pagata"
          >
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-red-700 dark:text-red-400 uppercase tracking-wider flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" /> Non pagate
                  </p>
                  <p className="text-2xl font-bold font-mono mt-1">{statPagamento.non_pagata.count}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 font-mono">{formatCurrency(statPagamento.non_pagata.totale)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dashboard: Stati di Allocazione (click to filter) */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
          <PieChart className="h-3.5 w-3.5" />{t('invoices.allocationStatus')}</p>
        <div className="grid gap-3 md:grid-cols-4">
          <Card
            className={`cursor-pointer hover:shadow-md transition border-l-4 border-l-green-500 ${filterAllocazione === "allocata" ? "ring-2 ring-green-400 bg-green-50/60 dark:bg-green-950/30" : ""}`}
            onClick={() => applyFilterAllocazione("allocata")}
            data-testid="stat-alloc-allocata"
          >
            <CardContent className="pt-4 pb-3">
              <p className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase tracking-wider flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" /> Allocate
              </p>
              <p className="text-2xl font-bold font-mono mt-1">{statAllocazione.allocata.count}</p>
              <p className="text-xs text-muted-foreground mt-0.5 font-mono">{formatCurrency(statAllocazione.allocata.totale)}</p>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer hover:shadow-md transition border-l-4 border-l-yellow-500 ${filterAllocazione === "parziale" ? "ring-2 ring-yellow-400 bg-yellow-50/60 dark:bg-yellow-950/30" : ""}`}
            onClick={() => applyFilterAllocazione("parziale")}
            data-testid="stat-alloc-parziale"
          >
            <CardContent className="pt-4 pb-3">
              <p className="text-xs font-semibold text-yellow-700 dark:text-yellow-400 uppercase tracking-wider flex items-center gap-1">
                <Layers className="h-3.5 w-3.5" /> Parziali
              </p>
              <p className="text-2xl font-bold font-mono mt-1">{statAllocazione.parziale.count}</p>
              <p className="text-xs text-muted-foreground mt-0.5 font-mono">{formatCurrency(statAllocazione.parziale.totale)}</p>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer hover:shadow-md transition border-l-4 border-l-blue-500 ${filterAllocazione === "in_allocazione" ? "ring-2 ring-blue-400 bg-blue-50/60 dark:bg-blue-950/30" : ""}`}
            onClick={() => applyFilterAllocazione("in_allocazione")}
            data-testid="stat-alloc-in-allocazione"
          >
            <CardContent className="pt-4 pb-3">
              <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wider flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />{t('common.inAllocation')}</p>
              <p className="text-2xl font-bold font-mono mt-1">{statAllocazione.in_allocazione.count}</p>
              <p className="text-xs text-muted-foreground mt-0.5 font-mono">{formatCurrency(statAllocazione.in_allocazione.totale)}</p>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer hover:shadow-md transition border-l-4 border-l-rose-500 ${filterAllocazione === "non_allocata" ? "ring-2 ring-rose-400 bg-rose-50/60 dark:bg-rose-950/30" : ""}`}
            onClick={() => applyFilterAllocazione("non_allocata")}
            data-testid="stat-alloc-non-allocata"
          >
            <CardContent className="pt-4 pb-3">
              <p className="text-xs font-semibold text-rose-700 dark:text-rose-400 uppercase tracking-wider flex items-center gap-1">
                <CircleDashed className="h-3.5 w-3.5" /> Da allocare
              </p>
              <p className="text-2xl font-bold font-mono mt-1">{statAllocazione.non_allocata.count}</p>
              <p className="text-xs text-muted-foreground mt-0.5 font-mono">{formatCurrency(statAllocazione.non_allocata.totale)}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 justify-end flex-wrap">
        {selectedIds.size > 0 && canEditFatture && (
          <Button variant="destructive" onClick={() => setDeleteMultipleDialog(true)}>
            <Trash2 className="h-4 w-4 mr-2" />
            Elimina Selezionati ({selectedIds.size})
          </Button>
        )}
        {invoices.length > 0 && canEditFatture && (
          <Button variant="destructive" onClick={() => setDeleteAllDialog(true)} data-testid="delete-all-btn">
            <Trash className="h-4 w-4 mr-2" />
            Elimina Tutto ({invoices.length})
          </Button>
        )}
        <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-template-btn">
          <Download className="h-4 w-4 mr-2" />
          Scarica Template
        </Button>
        {canEditFatture && (
          <>
            <ExcelUploadHelper
              columns={EXCEL_COLUMNS.scadenze}
              onUpload={handleUploadScadenze}
              uploading={uploadingScadenze}
              buttonText={t("invoices.uploadDeadlines")}
              buttonVariant="outline"
              title="Struttura File - Scadenze"
            />
            <ExcelUploadHelper
              columns={EXCEL_COLUMNS.fatture}
              onUpload={handleUpload}
              uploading={uploading}
              buttonText={t("leasing.uploadExcel")}
              buttonVariant="outline"
              title="Struttura File - Fatture"
            />
            <Button onClick={() => setNewInvoiceDialog(true)} data-testid="new-invoice-btn">
              <Plus className="h-4 w-4 mr-2" />{t('invoices.newInvoice')}</Button>
          </>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per ragione sociale o numero..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filterTipo} onValueChange={setFilterTipo}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="filter-tipo">
                <SelectValue placeholder={t("invoices.documentType")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.allTypes')}</SelectItem>
                <SelectItem value="FV">FV - Fattura Vendita</SelectItem>
                <SelectItem value="FA">FA - Fattura Acquisto</SelectItem>
                <SelectItem value="FVE">FVE - Fattura Vendita Estera</SelectItem>
                <SelectItem value="FAE">FAE - Fattura Acquisto Estera</SelectItem>
                <SelectItem value="NCV">NCV - Nota Credito Vendita</SelectItem>
                <SelectItem value="NCA">NCA - Nota Credito Acquisto</SelectItem>
                <SelectItem value="NCVE">NCVE - Nota Credito Vendita Estera</SelectItem>
                <SelectItem value="NCAE">NCAE - Nota Credito Acquisto Estera</SelectItem>
                <SelectItem value="NDV">NDV - Nota Debito Vendita</SelectItem>
                <SelectItem value="NDA">NDA - Nota Debito Acquisto</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStato} onValueChange={setFilterStato}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-stato">
                <SelectValue placeholder={t("invoices.paymentStatus")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.allStatusesText')}</SelectItem>
                <SelectItem value="pagata">{t('invoices.paid')}</SelectItem>
                <SelectItem value="parziale">{t('common.partial')}</SelectItem>
                <SelectItem value="non_pagata">Non pagata</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterAllocazione} onValueChange={setFilterAllocazione}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-allocazione">
                <SelectValue placeholder={t("invoices.allocationStatus")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="non_allocata">Da allocare</SelectItem>
                <SelectItem value="parziale">{t('common.partial')}</SelectItem>
                <SelectItem value="in_allocazione">{t('common.inAllocation')}</SelectItem>
                <SelectItem value="allocata">Allocata</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={filterCompensate ? "default" : "outline"}
              onClick={() => setFilterCompensate(v => !v)}
              className={filterCompensate ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
              data-testid="filter-compensate"
              title="Mostra solo fatture chiuse per compensazione"
            >
              <ArrowLeftRight className="h-4 w-4 mr-2" />
              Compensate
            </Button>
            <Button
              variant={showInlineFilters ? "secondary" : "outline"}
              size="sm"
              onClick={() => setShowInlineFilters(!showInlineFilters)}
              className="h-10"
            >
              <Filter className="h-4 w-4 mr-1" />
              Filtri Colonna
              {hasActiveInlineFilters && (
                <span className="ml-1 bg-primary text-primary-foreground rounded-full px-1.5 text-xs">
                  {Object.values(inlineFilters).filter(Boolean).length}
                </span>
              )}
            </Button>
            {hasActiveInlineFilters && (
              <Button variant="ghost" size="sm" onClick={clearInlineFilters} className="h-10">
                <X className="h-4 w-4 mr-1" />
                Pulisci
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="shadow-sm">
        <CardHeader className="pb-3 border-b bg-slate-50/50">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-indigo-600" />
              Elenco Fatture
              <Badge variant="secondary" className="ml-2 font-mono">
                {filteredInvoices.length}{hasActiveInlineFilters ? ` / ${invoices.length}` : ''}
              </Badge>
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                {/* Inline Filter Row */}
                {showInlineFilters && (
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <TableHead className="w-12" />
                    <TableHead className="w-20 py-2">
                      <Select
                        value={inlineFilters.tipo_documento || "all"}
                        onValueChange={(v) => setInlineFilters({ ...inlineFilters, tipo_documento: v === "all" ? "" : v })}
                      >
                        <SelectTrigger className="h-8 text-xs px-2" data-testid="inline-filter-tipo">
                          <SelectValue placeholder="Tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutti</SelectItem>
                          <SelectItem value="FV">FV</SelectItem>
                          <SelectItem value="FA">FA</SelectItem>
                          <SelectItem value="FVE">FVE</SelectItem>
                          <SelectItem value="FAE">FAE</SelectItem>
                          <SelectItem value="NCV">NCV</SelectItem>
                          <SelectItem value="NCA">NCA</SelectItem>
                          <SelectItem value="NCVE">NCVE</SelectItem>
                          <SelectItem value="NCAE">NCAE</SelectItem>
                          <SelectItem value="NDV">NDV</SelectItem>
                          <SelectItem value="NDA">NDA</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableHead>
                    <TableHead className="w-28 py-2">
                      <div className="flex flex-col gap-1">
                        <Input
                          placeholder="Dal DD-MM-YYYY"
                          value={inlineFilters.data_da}
                          onChange={(e) => setInlineFilters({ ...inlineFilters, data_da: e.target.value })}
                          className="h-7 text-xs"
                          data-testid="inline-filter-data-da"
                        />
                        <Input
                          placeholder="Al DD-MM-YYYY"
                          value={inlineFilters.data_a}
                          onChange={(e) => setInlineFilters({ ...inlineFilters, data_a: e.target.value })}
                          className="h-7 text-xs"
                          data-testid="inline-filter-data-a"
                        />
                      </div>
                    </TableHead>
                    <TableHead className="w-28 py-2">
                      <Input
                        placeholder="Numero..."
                        value={inlineFilters.numero}
                        onChange={(e) => setInlineFilters({ ...inlineFilters, numero: e.target.value })}
                        className="h-8 text-sm"
                        data-testid="inline-filter-numero"
                      />
                    </TableHead>
                    <TableHead className="min-w-[200px] py-2">
                      <Input
                        placeholder="Ragione sociale..."
                        value={inlineFilters.ragione_sociale}
                        onChange={(e) => setInlineFilters({ ...inlineFilters, ragione_sociale: e.target.value })}
                        className="h-8 text-sm"
                        data-testid="inline-filter-ragione"
                      />
                    </TableHead>
                    <TableHead className="w-28 py-2">
                      <Input
                        placeholder="Mod. Pag..."
                        value={inlineFilters.modalita_pagamento}
                        onChange={(e) => setInlineFilters({ ...inlineFilters, modalita_pagamento: e.target.value })}
                        className="h-8 text-sm"
                        data-testid="inline-filter-mod-pag"
                      />
                    </TableHead>
                    <TableHead className="w-28 py-2">
                      <Input
                        placeholder="Imponibile..."
                        value={inlineFilters.imponibile}
                        onChange={(e) => setInlineFilters({ ...inlineFilters, imponibile: e.target.value })}
                        className="h-8 text-sm text-right"
                        data-testid="inline-filter-imponibile"
                      />
                    </TableHead>
                    <TableHead className="w-24 py-2">
                      <Input
                        placeholder="IVA..."
                        value={inlineFilters.imposta}
                        onChange={(e) => setInlineFilters({ ...inlineFilters, imposta: e.target.value })}
                        className="h-8 text-sm text-right"
                        data-testid="inline-filter-iva"
                      />
                    </TableHead>
                    <TableHead className="w-28 py-2">
                      <Input
                        placeholder="Totale..."
                        value={inlineFilters.totale}
                        onChange={(e) => setInlineFilters({ ...inlineFilters, totale: e.target.value })}
                        className="h-8 text-sm text-right"
                        data-testid="inline-filter-totale"
                      />
                    </TableHead>
                    <TableHead className="w-28 py-2">
                      <Input
                        placeholder="Scadenza..."
                        value={inlineFilters.data_scadenza}
                        onChange={(e) => setInlineFilters({ ...inlineFilters, data_scadenza: e.target.value })}
                        className="h-8 text-sm"
                        data-testid="inline-filter-scadenza"
                      />
                    </TableHead>
                    <TableHead className="w-28 py-2">
                      <Input
                        placeholder="Pagato..."
                        value={inlineFilters.importo_pagato}
                        onChange={(e) => setInlineFilters({ ...inlineFilters, importo_pagato: e.target.value })}
                        className="h-8 text-sm text-right"
                        data-testid="inline-filter-pagato"
                      />
                    </TableHead>
                    <TableHead className="w-24 py-2">
                      <Select
                        value={inlineFilters.stato_pagamento || "all"}
                        onValueChange={(v) => setInlineFilters({ ...inlineFilters, stato_pagamento: v === "all" ? "" : v })}
                      >
                        <SelectTrigger className="h-8 text-xs px-2" data-testid="inline-filter-stato-pag">
                          <SelectValue placeholder="Stato" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutti</SelectItem>
                          <SelectItem value="pagata">Pagata</SelectItem>
                          <SelectItem value="parziale">Parziale</SelectItem>
                          <SelectItem value="non_pagata">Non pagata</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableHead>
                    <TableHead className="w-24 py-2">
                      <Select
                        value={inlineFilters.stato_allocazione || "all"}
                        onValueChange={(v) => setInlineFilters({ ...inlineFilters, stato_allocazione: v === "all" ? "" : v })}
                      >
                        <SelectTrigger className="h-8 text-xs px-2" data-testid="inline-filter-alloc">
                          <SelectValue placeholder="Alloc." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutte</SelectItem>
                          <SelectItem value="non_allocata">Da allocare</SelectItem>
                          <SelectItem value="parziale">Parziale</SelectItem>
                          <SelectItem value="in_allocazione">In alloc.</SelectItem>
                          <SelectItem value="allocata">Allocata</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableHead>
                    <TableHead className="w-32 py-2 text-right">
                      {hasActiveInlineFilters && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 text-xs"
                          onClick={clearInlineFilters}
                          data-testid="inline-filter-clear"
                        >
                          <X className="h-3 w-3 mr-1" />
                          Pulisci
                        </Button>
                      )}
                    </TableHead>
                  </TableRow>
                )}
                <TableRow className="bg-slate-50 border-y">
                  <TableHead className="w-12">
                    <Checkbox
                      checked={isAllSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = isSomeSelected;
                      }}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Seleziona tutto"
                    />
                  </TableHead>
                  <TableHead className="w-20 text-xs font-semibold text-slate-600">
                    <SortableHeader field="tipo_documento" currentSort={sort} onSort={setSort}>{t("common.type")}</SortableHeader>
                  </TableHead>
                  <TableHead className="w-28 text-xs font-semibold text-slate-600">
                    <SortableHeader field="data" currentSort={sort} onSort={setSort}>{t("common.date")}</SortableHeader>
                  </TableHead>
                  <TableHead className="w-28 text-xs font-semibold text-slate-600">
                    <SortableHeader field="numero" currentSort={sort} onSort={setSort}>{t('invoices.invoiceNumber')}</SortableHeader>
                  </TableHead>
                  <TableHead className="min-w-[200px] text-xs font-semibold text-slate-600">
                    <SortableHeader field="ragione_sociale" currentSort={sort} onSort={setSort}>{t('common.company')}</SortableHeader>
                  </TableHead>
                  <TableHead className="w-28 text-xs font-semibold text-slate-600">
                    <SortableHeader field="modalita_pagamento" currentSort={sort} onSort={setSort}>Modalità</SortableHeader>
                  </TableHead>
                  <TableHead className="w-28 text-right text-xs font-semibold text-slate-600">
                    <SortableHeader field="imponibile" currentSort={sort} onSort={setSort} align="right">{t('invoices.taxableAmount')}</SortableHeader>
                  </TableHead>
                  <TableHead className="w-24 text-right text-xs font-semibold text-slate-600">
                    <SortableHeader field="imposta" currentSort={sort} onSort={setSort} align="right">IVA</SortableHeader>
                  </TableHead>
                  <TableHead className="w-28 text-right text-xs font-semibold text-slate-600">
                    <SortableHeader field="totale" currentSort={sort} onSort={setSort} align="right">{t("common.total")}</SortableHeader>
                  </TableHead>
                  <TableHead className="w-28 text-xs font-semibold text-slate-600">
                    <SortableHeader field="data_scadenza" currentSort={sort} onSort={setSort}>{t('invoices.dueDate')}</SortableHeader>
                  </TableHead>
                  <TableHead className="w-28 text-right text-xs font-semibold text-slate-600">
                    <SortableHeader field="importo_pagato" currentSort={sort} onSort={setSort} align="right">{t('invoices.paid')}</SortableHeader>
                  </TableHead>
                  <TableHead className="w-24 text-xs font-semibold text-slate-600">
                    <SortableHeader field="stato_pagamento" currentSort={sort} onSort={setSort}>{t("common.status")}</SortableHeader>
                  </TableHead>
                  <TableHead className="w-24 text-xs font-semibold text-slate-600">
                    <SortableHeader field="stato_allocazione" currentSort={sort} onSort={setSort}>Alloc.</SortableHeader>
                  </TableHead>
                  <TableHead className="w-32 text-right text-xs font-semibold text-slate-600">{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} className="text-center py-12 text-muted-foreground">
                      Nessuna fattura trovata
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedInvoices.map((inv, index) => (
                    <TableRow 
                      key={inv.id} 
                      data-testid={`invoice-row-${inv.id}`}
                      className={`
                        ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}
                        ${selectedIds.has(inv.id) ? "bg-blue-50/50 border-l-2 border-l-blue-500" : ""} 
                        hover:bg-slate-100/80 transition-colors
                      `}
                    >
                      <TableCell className="py-3">
                        <Checkbox
                          checked={selectedIds.has(inv.id)}
                          onCheckedChange={() => toggleSelect(inv.id)}
                          aria-label={`Seleziona fattura ${inv.numero}`}
                        />
                      </TableCell>
                      <TableCell className="py-3">
                        <span className="text-xs font-medium text-slate-500 uppercase">{inv.tipo_documento?.substring(0, 8)}</span>
                      </TableCell>
                      <TableCell className="py-3">
                        <span className="font-mono text-sm">{inv.data}</span>
                      </TableCell>
                      <TableCell className="py-3">
                        <span className="font-mono text-sm font-semibold">{inv.numero}</span>
                      </TableCell>
                      <TableCell className="py-3">
                        <span className="text-sm font-medium text-slate-700 block max-w-[200px] truncate" title={inv.ragione_sociale || inv.fornitore_cliente}>
                          {inv.ragione_sociale || inv.fornitore_cliente}
                        </span>
                      </TableCell>
                      <TableCell className="py-3">
                        <span className="text-xs text-slate-500">{inv.modalita_pagamento || "-"}</span>
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        <span className="font-mono text-sm tabular-nums">{formatCurrency(inv.imponibile || 0)}</span>
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        <span className="font-mono text-sm tabular-nums text-slate-500">{formatCurrency(inv.iva || 0)}</span>
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        <span className="font-mono text-sm tabular-nums font-bold">{formatCurrency(inv.totale)}</span>
                      </TableCell>
                      <TableCell className="py-3">
                        <div className="flex items-center gap-1">
                          <span className="font-mono text-sm text-blue-600 font-medium">
                            {inv.prossima_scadenza || inv.data_scadenza || "-"}
                          </span>
                          {inv.num_rimanenti > 1 && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-purple-50 text-purple-700 border-purple-200">
                              +{inv.num_rimanenti - 1}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        <span className={`font-mono text-sm tabular-nums flex items-center justify-end gap-1 ${hasAnomaly(inv) ? "text-orange-600" : "text-green-600"}`}>
                          {hasAnomaly(inv) && <AlertTriangle className="h-3.5 w-3.5" title="Pagato > Totale" />}
                          {formatCurrency(inv.importo_pagato)}
                        </span>
                      </TableCell>
                      <TableCell className="py-3">
                        <div className="flex flex-col gap-1 items-start">
                          {getStatusBadge(inv.stato_pagamento)}
                          {inv.compensata && (
                            <Badge variant="outline" className="text-xs gap-1 border-blue-400 text-blue-700 bg-blue-50 dark:bg-blue-950/30">
                              <ArrowLeftRight className="h-3 w-3" />Compensata
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-3">{getAllocStatusBadge(inv.stato_allocazione)}</TableCell>
                      <TableCell className="py-3">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => openEditInvoice(inv)}
                            title={t("invoices.editInvoice")}
                            data-testid={`edit-btn-${inv.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => openScadenzaDialog(inv)}
                            title={t("invoices.manageDueDates")}
                            data-testid={`scadenza-btn-${inv.id}`}
                          >
                            <CalendarDays className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => {
                              setPaymentDialog({ open: true, invoice: inv });
                              setPaymentForm({
                                importo: String(Math.max(0, Math.abs(inv.totale) - inv.importo_pagato).toFixed(2)),
                                data_pagamento: new Date().toISOString().split("T")[0],
                                note: "",
                              });
                            }}
                            title={t("invoices.recordPayment")}
                            data-testid={`pay-btn-${inv.id}`}
                          >
                            <Euro className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => openAllocDialog(inv)}
                            title="Alloca su conti/centri costo"
                            data-testid={`alloc-btn-${inv.id}`}
                          >
                            <Layers className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => setDeleteOneDialog({ open: true, id: inv.id })}
                            data-testid={`delete-btn-${inv.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination Controls */}
          <div className="flex items-center justify-between pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              Pagina {currentPage} di {totalPages || 1} ({sortedInvoices.length} di {invoices.length} fatture)
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />{t('common.previous')}</Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >{t('common.nextLabel')}<ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog.open} onOpenChange={(open) => setPaymentDialog({ open, invoice: null })}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>{t("treasury.registerPayment")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-muted p-3 rounded-md">
              <p className="text-sm text-muted-foreground">Fattura: {paymentDialog.invoice?.numero}</p>
              <p className="text-sm font-medium">{paymentDialog.invoice?.ragione_sociale || paymentDialog.invoice?.fornitore_cliente}</p>
              <p className="text-sm">Da pagare: {formatCurrency(Math.max(0, Math.abs(paymentDialog.invoice?.totale || 0) - (paymentDialog.invoice?.importo_pagato || 0)))}</p>
            </div>
            <div className="space-y-2">
              <Label>{t("common.amount")}</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentForm.importo}
                onChange={(e) => setPaymentForm({ ...paymentForm, importo: e.target.value })}
                data-testid="payment-importo"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('invoices.paymentDate')}</Label>
              <DateInput
                value={paymentForm.data_pagamento}
                onChange={(e) => setPaymentForm({ ...paymentForm, data_pagamento: e.target.value })}
                data-testid="payment-data"
              />
            </div>
            <div className="space-y-2">
              <Label>Note (opzionale)</Label>
              <Input
                value={paymentForm.note}
                onChange={(e) => setPaymentForm({ ...paymentForm, note: e.target.value })}
                placeholder={t("common.notes")}
                data-testid="payment-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialog({ open: false, invoice: null })} disabled={savingPayment}>{t('common.cancel')}</Button>
            <Button onClick={handlePayment} data-testid="payment-submit" disabled={savingPayment}>
              {savingPayment ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("common.saving")}</> : 'Registra'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocation Dialog */}
      <Dialog open={allocDialog.open} onOpenChange={(open) => setAllocDialog({ open, invoice: null, allocations: [] })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="alloc-dialog">
          <DialogHeader>
            <DialogTitle>{t("invoices.allocateInvoice")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Invoice summary */}
            <div className="bg-muted p-3 rounded-md">
              <div className="flex justify-between flex-wrap gap-2">
                <div>
                  <p className="text-sm text-muted-foreground">Fattura: {currentInvoice?.numero}</p>
                  <p className="text-sm font-medium">{currentInvoice?.ragione_sociale || currentInvoice?.fornitore_cliente}</p>
                </div>
                <div className="text-right space-y-1">
                  <p className="text-xs text-muted-foreground">Imponibile: {formatCurrency(Math.abs(currentInvoice?.imponibile || 0))}</p>
                  <p className="text-xs text-muted-foreground">IVA: {formatCurrency(Math.abs(currentInvoice?.iva || 0))}</p>
                  <p className="text-sm font-bold text-blue-600">Residuo Imponibile: {formatCurrency(residuoImponibile)}</p>
                  <p className="text-sm font-bold text-orange-600">Residuo IVA: {formatCurrency(residuoIva)}</p>
                </div>
              </div>
            </div>

            {/* Sezione Nota di Credito → Fattura di riferimento */}
            {(currentInvoice?.tipo_documento === 'NCA' || currentInvoice?.tipo_documento === 'NCAE') && (
              <div className="p-4 border-2 border-amber-300 rounded-lg bg-amber-50/40" data-testid="nca-section">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-5 w-5 text-amber-700" />
                  <h4 className="font-semibold text-amber-900 text-sm">Nota di Credito Acquisto — Associazione a Fattura</h4>
                </div>
                {currentInvoice?.invoice_id_riferimento ? (
                  <div className="space-y-2">
                    <p className="text-sm text-amber-900">
                      Questa NC è associata alla fattura di riferimento. La compensazione è stata applicata:
                      se |NC| ≥ residuo → chiusura totale (stato <code>chiusa_da_nca</code>); altrimenti riduzione del residuo.
                    </p>
                    <Button variant="outline" size="sm" onClick={handleUnlinkNc} disabled={linkingNc} data-testid="unlink-nc-btn">
                      {linkingNc ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <X className="h-3 w-3 mr-1" />}
                      Rimuovi associazione
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-amber-800">
                      Seleziona la fattura passiva (FA/FAE) dello stesso fornitore a cui questa NC si riferisce.
                      Se |NC| ≥ residuo fattura → chiusura totale. Altrimenti chiusura parziale (il residuo della fattura resta aperto ridotto di |NC|).
                    </p>
                    {fattureAperte.length === 0 ? (
                      <p className="text-xs text-muted-foreground italic">
                        Nessuna fattura aperta trovata per il fornitore &quot;{currentInvoice?.fornitore_cliente}&quot;.
                      </p>
                    ) : (
                      <>
                        <SearchableSelect
                          options={fattureAperte.map(f => ({
                            value: f.id,
                            label: `${f.tipo_documento} n.${f.numero} (${f.data}) - Residuo ${formatCurrency(f.residuo)}`
                          }))}
                          value={selectedFatturaRif}
                          onValueChange={setSelectedFatturaRif}
                          placeholder="Seleziona fattura di riferimento..."
                          searchPlaceholder="Cerca per numero..."
                          testId="select-fattura-riferimento"
                        />
                        <Button
                          size="sm"
                          onClick={handleLinkNc}
                          disabled={!selectedFatturaRif || linkingNc}
                          data-testid="link-nc-btn"
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                        >
                          {linkingNc ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                          Associa e compensa
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Existing allocations */}
            {allocDialog.allocations.length > 0 && (
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("common.type")}</TableHead>
                      <TableHead>{t('movements.account')}</TableHead>
                      <TableHead>Centro Costo</TableHead>
                      <TableHead>Competenza</TableHead>
                      <TableHead className="text-right">{t("common.amount")}</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allocDialog.allocations.map((alloc) => (
                      <TableRow key={alloc.id} className={editAllocId === alloc.id ? "bg-muted" : ""}>
                        <TableCell>
                          <Badge variant={(alloc.descrizione || "").includes("iva_indeducibile") ? "secondary" : "default"} className="text-xs">
                            {(alloc.descrizione || "").includes("iva_indeducibile") ? "IVA Ind." : "Imponibile"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{alloc.conto_nome || getAccountName(alloc.conto_id) || "-"}</TableCell>
                        <TableCell className="text-sm">{alloc.centro_costo_nome || getCostCenterName(alloc.centro_costo_id) || "-"}</TableCell>
                        <TableCell className="text-xs font-mono">
                          {alloc.data_inizio_competenza && alloc.data_fine_competenza 
                            ? `${alloc.data_inizio_competenza} → ${alloc.data_fine_competenza}`
                            : alloc.data_inizio_competenza || "-"}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          <div>
                            <div>{formatCurrency(alloc.importo)}</div>
                            {alloc.iva_indeducibile > 0 && (
                              <div className="text-xs text-orange-600">
                                + IVA Ind. {formatCurrency(alloc.iva_indeducibile)}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditAllocation(alloc)}
                              className="text-blue-600 hover:text-blue-700"
                              title={t("invoices.editAllocation")}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAllocation(alloc.id)}
                              className="text-destructive"
                              title={t("invoices.deleteAllocation")}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Add/Edit allocation form - mostra sempre se in modifica o se c'è residuo */}
            {(residuoImponibile > 0.01 || residuoIva > 0.01 || editAllocId) && (
              <div className={`border rounded-md p-4 space-y-4 ${editAllocId ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : ''}`}>
                <div className="flex justify-between items-center">
                  <h4 className="font-medium">
                    {editAllocId ? t("invoices.editAllocation") : t("invoices.addAllocation")}
                  </h4>
                  {editAllocId && (
                    <Button variant="ghost" size="sm" onClick={handleCancelEdit}>
                      <X className="h-4 w-4 mr-1" />{t('dialogs.cancelEdit')}</Button>
                  )}
                </div>
                
                {/* Tipo allocazione */}
                <div className="space-y-2">
                  <Label>Tipo Allocazione *</Label>
                  <Select 
                    value={allocForm.tipo} 
                    onValueChange={(v) => {
                      const isBoll = ['bollette', 'manutenzioni_appartamenti'].includes(allocForm.tipo_allocazione);
                      const newResiduo = isBoll ? (residuoImponibile + residuoIva) : (v === "iva_indeducibile" ? residuoIva : residuoImponibile);
                      if (!editAllocId) {
                        setAllocForm({ ...allocForm, tipo: v, importo: String(Math.max(0, newResiduo).toFixed(2)) });
                      } else {
                        setAllocForm({ ...allocForm, tipo: v });
                      }
                    }}
                    disabled={['bollette', 'manutenzioni_appartamenti'].includes(allocForm.tipo_allocazione)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="imponibile">
                        Imponibile {!editAllocId && `(residuo: ${formatCurrency(residuoImponibile)})`}
                      </SelectItem>
                      <SelectItem value="iva_indeducibile">
                        IVA Indetraibile {!editAllocId && `(residuo: ${formatCurrency(residuoIva)})`}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Conto {['bollette', 'manutenzioni_appartamenti'].includes(allocForm.tipo_allocazione) ? '' : '*'} {allocForm.genera_ammortamento && (allocForm.asset_id || allocForm.automezzo_id) && <span className="text-xs text-purple-600">(dalla categoria)</span>}</Label>
                    <SearchableSelect
                      options={accounts.map(a => ({ value: a.id, label: `${a.codice} - ${a.nome}` }))}
                      value={allocForm.account_id}
                      onValueChange={(v) => setAllocForm({ ...allocForm, account_id: v })}
                      placeholder={t("dialogs.selectAccount")}
                      searchPlaceholder={t("common.searchPlaceholder")}
                      emptyMessage="Nessun conto trovato"
                      disabled={['bollette', 'manutenzioni_appartamenti'].includes(allocForm.tipo_allocazione) || (allocForm.genera_ammortamento && (allocForm.asset_id || allocForm.automezzo_id))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Centro di Costo / Destinazione {allocForm.genera_ammortamento && (allocForm.asset_id || allocForm.automezzo_id) && <span className="text-xs text-purple-600">(dalla locazione bene)</span>}</Label>
                    <SearchableSelect
                      options={[
                        { value: "none", label: "Nessuno (allocazione standard)" },
                        { value: "bollette", label: "⚡ Bollette (da assegnare su appartamento)" },
                        { value: "manutenzioni_appartamenti", label: "🔧 Manutenzioni Appartamenti" },
                        { value: "carburanti", label: "⛽ Carburanti (da assegnare su veicolo)" },
                        { value: "manutenzioni_automezzi", label: "🚗 Manutenzioni Automezzi" },
                        ...costCenters.map(cc => ({ value: cc.id, label: `${cc.codice} - ${cc.nome}` }))
                      ]}
                      value={allocForm.cost_center_id || allocForm.tipo_allocazione || "none"}
                      onValueChange={(v) => {
                        const tipiBollette = ['bollette', 'manutenzioni_appartamenti'];
                        const tipiVeicoliDisabilita = ['carburanti', 'manutenzioni_automezzi'];
                        const tipiSpeciali = [...tipiBollette, ...tipiVeicoliDisabilita];
                        if (tipiBollette.includes(v)) {
                          // Per bollette/manutenzioni: importo = totale fattura (imponibile + IVA)
                          const residuoTot = residuoImponibile + residuoIva;
                          setAllocForm({ ...allocForm, cost_center_id: "", tipo_allocazione: v, account_id: "", data_inizio_competenza: "", data_fine_competenza: "", note: "", iva_indeducibile: "", importo: String(Math.max(0, residuoTot).toFixed(2)) });
                        } else if (tipiVeicoliDisabilita.includes(v)) {
                          // Per carburanti/manutenzioni automezzi: disabilita CDC e date competenza
                          setAllocForm({ ...allocForm, cost_center_id: "", tipo_allocazione: v, data_inizio_competenza: "", data_fine_competenza: "" });
                        } else if (v === "none") {
                          setAllocForm({ ...allocForm, cost_center_id: "", tipo_allocazione: "standard" });
                        } else {
                          setAllocForm({ ...allocForm, cost_center_id: v, tipo_allocazione: "standard" });
                        }
                      }}
                      placeholder="Seleziona destinazione"
                      searchPlaceholder="Cerca centro costo..."
                      emptyMessage="Nessun centro trovato"
                      disabled={allocForm.genera_ammortamento && (allocForm.asset_id || allocForm.automezzo_id)}
                    />
                    {allocForm.tipo_allocazione === "bollette" && (
                      <p className="text-xs text-blue-600">
                        ⚡ I campi sono disabilitati. L&apos;importo verrà suddiviso per appartamento nella tab Bollette.
                      </p>
                    )}
                    {allocForm.tipo_allocazione === "manutenzioni_appartamenti" && (
                      <p className="text-xs text-orange-600">
                        🔧 I campi sono disabilitati. L&apos;importo verrà suddiviso per appartamento nella tab Bollette.
                      </p>
                    )}
                    {allocForm.tipo_allocazione === "carburanti" && (
                      <p className="text-xs text-green-600">
                        I campi CDC e date competenza sono disabilitati. La data competenza sarà determinata dalla data rifornimento nella tab Carburanti.
                      </p>
                    )}
                    {allocForm.tipo_allocazione === "manutenzioni_automezzi" && (
                      <p className="text-xs text-purple-600">
                        Questa allocazione sarà collegabile a una manutenzione veicolo esistente
                      </p>
                    )}
                  </div>
                </div>

                {/* Date competenza */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data Inizio Competenza {allocForm.genera_ammortamento && (allocForm.asset_id || allocForm.automezzo_id) && <span className="text-xs text-purple-600">(dalla data fattura)</span>}</Label>
                    <Input
                      type="text"
                      placeholder="GG-MM-AAAA"
                      value={allocForm.genera_ammortamento && (allocForm.asset_id || allocForm.automezzo_id) ? (allocDialog.invoice?.data || '') : allocForm.data_inizio_competenza}
                      onChange={(e) => setAllocForm({ ...allocForm, data_inizio_competenza: e.target.value })}
                      disabled={['bollette', 'manutenzioni_appartamenti', 'carburanti', 'manutenzioni_automezzi'].includes(allocForm.tipo_allocazione) || (allocForm.genera_ammortamento && (allocForm.asset_id || allocForm.automezzo_id))}
                      className={['bollette', 'manutenzioni_appartamenti', 'carburanti', 'manutenzioni_automezzi'].includes(allocForm.tipo_allocazione) || (allocForm.genera_ammortamento && (allocForm.asset_id || allocForm.automezzo_id)) ? "bg-gray-100 dark:bg-gray-800" : ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Data Fine Competenza</Label>
                    <Input
                      type="text"
                      placeholder="GG-MM-AAAA"
                      value={allocForm.data_fine_competenza}
                      onChange={(e) => setAllocForm({ ...allocForm, data_fine_competenza: e.target.value })}
                      disabled={['bollette', 'manutenzioni_appartamenti', 'carburanti', 'manutenzioni_automezzi'].includes(allocForm.tipo_allocazione) || (allocForm.genera_ammortamento && (allocForm.asset_id || allocForm.automezzo_id))}
                      className={['bollette', 'manutenzioni_appartamenti', 'carburanti', 'manutenzioni_automezzi'].includes(allocForm.tipo_allocazione) || (allocForm.genera_ammortamento && (allocForm.asset_id || allocForm.automezzo_id)) ? "bg-gray-100 dark:bg-gray-800" : ""}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Importo Imponibile *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={allocForm.importo}
                      onChange={(e) => setAllocForm({ ...allocForm, importo: e.target.value })}
                      disabled={['bollette', 'manutenzioni_appartamenti'].includes(allocForm.tipo_allocazione)}
                      className={['bollette', 'manutenzioni_appartamenti'].includes(allocForm.tipo_allocazione) ? "bg-gray-100 dark:bg-gray-800" : ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>IVA Indetraibile</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={allocForm.iva_indeducibile}
                      onChange={(e) => setAllocForm({ ...allocForm, iva_indeducibile: e.target.value })}
                      placeholder="0.00"
                      disabled={['bollette', 'manutenzioni_appartamenti'].includes(allocForm.tipo_allocazione)}
                      className={['bollette', 'manutenzioni_appartamenti'].includes(allocForm.tipo_allocazione) ? "bg-gray-100 dark:bg-gray-800" : ""}
                    />
                    <p className="text-xs text-muted-foreground">Quota IVA non detraibile, va sullo stesso conto</p>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("common.notes")}</Label>
                    <Input
                      value={allocForm.note}
                      onChange={(e) => setAllocForm({ ...allocForm, note: e.target.value })}
                      placeholder={t("common.notes")}
                      disabled={['bollette', 'manutenzioni_appartamenti'].includes(allocForm.tipo_allocazione)}
                      className={['bollette', 'manutenzioni_appartamenti'].includes(allocForm.tipo_allocazione) ? "bg-gray-100 dark:bg-gray-800" : ""}
                    />
                  </div>
                </div>
                
                {/* Sezione Ammortamento */}
                {!editAllocId && !['bollette', 'manutenzioni_appartamenti'].includes(allocForm.tipo_allocazione) && (
                  <div className={`border rounded-md p-3 space-y-3 ${allocForm.genera_ammortamento ? 'border-purple-300 bg-purple-50 dark:bg-purple-950' : 'border-dashed'}`}>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="genera_ammortamento"
                        checked={allocForm.genera_ammortamento}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setAllocForm({ 
                              ...allocForm, 
                              genera_ammortamento: true,
                              categoria_ammortamento_id: allocForm.categoria_ammortamento_id || "",
                              descrizione_bene: allocForm.descrizione_bene || "",
                              asset_id: "",
                              automezzo_id: ""
                            });
                          } else {
                            setAllocForm({ 
                              ...allocForm, 
                              genera_ammortamento: false,
                              categoria_ammortamento_id: "",
                              descrizione_bene: "",
                              asset_id: "",
                              automezzo_id: ""
                            });
                          }
                        }}
                      />
                      <Label htmlFor="genera_ammortamento" className="text-sm cursor-pointer">
                        Genera Ammortamento (immobilizzazione)
                      </Label>
                    </div>
                    
                    {allocForm.genera_ammortamento && (
                      <div className="space-y-3 pt-2">
                        {/* Selezione Asset */}
                        <div className="space-y-2">
                          <Label>Asset Aziendale</Label>
                          <SearchableSelect
                            options={beniDisponibili.assets.map(a => ({ 
                              value: a.id, 
                              label: a.label + (a.centro_costo_nome ? ` → ${a.centro_costo_codice || ''} ${a.centro_costo_nome}` : ' (senza locazione)')
                            }))}
                            value={allocForm.asset_id}
                            onValueChange={(v) => {
                              const asset = beniDisponibili.assets.find(a => a.id === v);
                              const categoria = categorieImmobilizzazioni.find(c => c.nome?.toLowerCase().includes('attrezzatura') || c.nome?.toLowerCase().includes('asset'));
                              setAllocForm({ 
                                ...allocForm, 
                                asset_id: v,
                                automezzo_id: "", // Deseleziona automezzo
                                descrizione_bene: asset ? `${asset.tipologia || 'Asset'}: ${asset.marca} ${asset.modello}` : allocForm.descrizione_bene,
                                // Auto-compila CDC se l'asset ha una locazione
                                cost_center_id: asset?.centro_costo_id || beniDisponibili.cdc_non_allocabile?.id || allocForm.cost_center_id,
                                // Auto-compila conto dalla categoria se presente
                                account_id: categoria?.conto_id || allocForm.account_id
                              });
                            }}
                            placeholder="Seleziona asset..."
                            searchPlaceholder="Cerca asset..."
                            emptyMessage="Nessun asset trovato"
                          />
                        </div>
                        
                        {/* Selezione Automezzo */}
                        <div className="space-y-2">
                          <Label>Automezzo</Label>
                          <SearchableSelect
                            options={beniDisponibili.automezzi.map(a => ({ 
                              value: a.id, 
                              label: a.label + (a.centro_costo_nome ? ` → ${a.centro_costo_codice || ''} ${a.centro_costo_nome}` : ' (senza locazione)')
                            }))}
                            value={allocForm.automezzo_id}
                            onValueChange={(v) => {
                              const automezzo = beniDisponibili.automezzi.find(a => a.id === v);
                              const categoria = categorieImmobilizzazioni.find(c => c.nome?.toLowerCase().includes('autom') || c.nome?.toLowerCase().includes('veicol'));
                              setAllocForm({ 
                                ...allocForm, 
                                automezzo_id: v,
                                asset_id: "", // Deseleziona asset
                                descrizione_bene: automezzo ? `Automezzo: ${automezzo.marca} ${automezzo.modello} (${automezzo.targa})` : allocForm.descrizione_bene,
                                // Auto-compila CDC se l'automezzo ha una locazione
                                cost_center_id: automezzo?.centro_costo_id || beniDisponibili.cdc_non_allocabile?.id || allocForm.cost_center_id,
                                // Auto-compila conto dalla categoria se presente
                                account_id: categoria?.conto_id || allocForm.account_id
                              });
                            }}
                            placeholder={t("dialogs.selectVehicle")}
                            searchPlaceholder="Cerca automezzo..."
                            emptyMessage="Nessun automezzo trovato"
                          />
                        </div>
                        
                        {/* Messaggio se bene selezionato */}
                        {(allocForm.asset_id || allocForm.automezzo_id) && (
                          <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded text-xs text-purple-700 dark:text-purple-300">
                            <p className="font-medium">Campi auto-compilati:</p>
                            <ul className="list-disc list-inside mt-1">
                              <li>Centro di Costo: dalla locazione del bene</li>
                              <li>Conto: dalla categoria di ammortamento</li>
                              <li>Data Competenza: dalla data fattura</li>
                            </ul>
                          </div>
                        )}
                        
                        {/* Categoria Immobilizzazione */}
                        <div className="space-y-2">
                          <Label>Categoria Immobilizzazione *</Label>
                          <SearchableSelect
                            options={categorieImmobilizzazioni.map(c => ({ 
                              value: c.id, 
                              label: `${c.nome} (${c.quota_annuale_percentuale}% annuo)${c.conto_nome ? ` → ${c.conto_codice || ''} ${c.conto_nome}` : ''}`
                            }))}
                            value={allocForm.categoria_ammortamento_id}
                            onValueChange={(v) => {
                              const cat = categorieImmobilizzazioni.find(c => c.id === v);
                              setAllocForm({ 
                                ...allocForm, 
                                categoria_ammortamento_id: v,
                                // Auto-compila conto dalla categoria
                                account_id: cat?.conto_id || allocForm.account_id
                              });
                            }}
                            placeholder={t("common.selectPlaceholder")}
                            searchPlaceholder="Cerca categoria..."
                            emptyMessage="Nessuna categoria trovata"
                          />
                        </div>
                        
                        {/* Descrizione Bene */}
                        <div className="space-y-2">
                          <Label>Descrizione Bene *</Label>
                          <Input
                            value={allocForm.descrizione_bene}
                            onChange={(e) => setAllocForm({ ...allocForm, descrizione_bene: e.target.value })}
                            placeholder="Es: Saldatrice Lincoln Invertec V270"
                          />
                        </div>
                        
                        <p className="text-xs text-purple-600">
                          L&apos;ammortamento partirà dalla data fattura ({allocDialog.invoice?.data || 'N/A'}). Il primo anno sarà calcolato al 50% della quota annuale.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                <Button onClick={handleAddAllocation} className="w-full" disabled={!canAddAllocation}>
                  {editAllocId ? (
                    <>
                      <Pencil className="h-4 w-4 mr-2" />{t('common.saveChanges')}</>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />{t('common.add')}</>
                  )}
                </Button>
              </div>
            )}

            {residuoImponibile <= 0.01 && residuoIva <= 0.01 && (
              <div className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 p-3 rounded-md text-center">
                Fattura completamente allocata
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {(residuoImponibile > 0.01 || residuoIva > 0.01) && currentInvoice && (
              <div className="flex items-center gap-2 mr-auto">
                <input 
                  type="checkbox" 
                  id="forza-allocata"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={(currentInvoice?.stato_allocazione === 'allocata')}
                  onChange={async (e) => {
                    try {
                      await api.put(`${getApiPrefix()}/invoices/${currentInvoice.id}/forza-stato-allocazione?allocata=${e.target.checked}`);
                      showSuccess(e.target.checked ? "Fattura forzata come allocata" : "Stato allocazione ripristinato");
                      fetchData();
                      // Aggiorna l'invoice nel dialog
                      setAllocDialog(prev => ({ ...prev, invoice: { ...prev.invoice, stato_allocazione: e.target.checked ? 'allocata' : 'non_allocata' }}));
                    } catch (err) { handleApiError(err); }
                  }}
                  data-testid="forza-allocata-checkbox"
                />
                <Label htmlFor="forza-allocata" className="text-sm cursor-pointer">Forza come allocata</Label>
              </div>
            )}
            <Button variant="outline" onClick={() => { setAllocDialog({ open: false, invoice: null, allocations: [] }); setEditAllocId(null); }}>{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog open={deleteAllDialog} onOpenChange={(open) => { setDeleteAllDialog(open); if (!open) setDeleteAllConfirmText(""); }}>
        <DialogContent data-testid="delete-all-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Azione IRREVERSIBILE
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-2">
                <div className="bg-red-50 border-2 border-red-300 rounded p-3">
                  <p className="text-red-900 font-semibold">
                    Stai per eliminare <span className="text-lg">{invoices.length}</span> fatture, insieme a TUTTE le loro allocazioni, rate e pagamenti.
                  </p>
                  <p className="text-red-800 text-sm mt-1">Questa azione NON può essere annullata.</p>
                </div>
                <div>
                  <p className="text-sm mb-2">
                    Per confermare digita esattamente lo slug del tenant: <code className="bg-slate-100 px-1 rounded text-red-700 font-mono">{(window.location.pathname.split('/').filter(Boolean)[0] || '').toLowerCase()}</code>
                  </p>
                  <input
                    type="text"
                    className="w-full border-2 border-red-300 rounded px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-red-400"
                    placeholder="digita qui lo slug del tenant"
                    value={deleteAllConfirmText}
                    onChange={(e) => setDeleteAllConfirmText(e.target.value)}
                    data-testid="delete-all-confirm-input"
                    autoComplete="off"
                  />
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteAllDialog(false); setDeleteAllConfirmText(""); }} disabled={deletingInvoice === 'all'}>{t('common.cancel')}</Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAll}
              data-testid="confirm-delete-all"
              disabled={deletingInvoice === 'all' || deleteAllConfirmText.trim().toLowerCase() !== (window.location.pathname.split('/').filter(Boolean)[0] || '').toLowerCase()}
            >
              {deletingInvoice === 'all' ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('common.deleting')}</> : 'Elimina Tutto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Single Invoice Confirmation Dialog */}
      <Dialog open={deleteOneDialog.open} onOpenChange={(open) => setDeleteOneDialog({ open, id: null })}>
        <DialogContent data-testid="delete-one-dialog">
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare questa fattura?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOneDialog({ open: false, id: null })} disabled={deletingInvoice}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteOneDialog.id)} data-testid="confirm-delete-one" disabled={deletingInvoice}>
              {deletingInvoice ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('common.deleting')}</> : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scadenze Dialog */}
      <Dialog open={scadenzaDialog.open} onOpenChange={(open) => setScadenzaDialog({ open, invoice: null })}>
        <DialogContent className="max-w-2xl" data-testid="scadenza-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />{t('invoices.invoiceDueDates')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            {/* Info fattura */}
            <div className="bg-muted p-3 rounded-md flex justify-between items-start">
              <div>
                <p className="text-sm text-muted-foreground">Fattura: {scadenzaDialog.invoice?.numero}</p>
                <p className="text-sm font-medium">{scadenzaDialog.invoice?.ragione_sociale || scadenzaDialog.invoice?.fornitore_cliente}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">{t('invoices.invoiceTotal')}</p>
                <p className="text-lg font-mono font-bold">{formatCurrency(scadenzaDialog.invoice?.totale)}</p>
              </div>
            </div>

            {/* Modalità di Pagamento */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label>{t('invoices.paymentMethod')}</Label>
                <Button size="sm" variant="outline" onClick={handleUpdateScadenza} data-testid="scadenza-submit">
                  Salva Modalità
                </Button>
              </div>
              <SearchableSelect
                options={[
                  { value: "none", label: "-- Nessuna --" },
                  ...paymentMethods.map(pm => ({ value: pm.nome, label: `${pm.codice} - ${pm.nome}` }))
                ]}
                value={scadenzaForm.modalita_pagamento || "none"}
                onValueChange={(v) => setScadenzaForm({ ...scadenzaForm, modalita_pagamento: v === "none" ? "" : v })}
                placeholder="Seleziona modalità"
                searchPlaceholder="Cerca modalità..."
                emptyMessage="Nessuna modalità trovata"
                testId="scadenza-modalita"
              />
            </div>

            {/* Lista Scadenze */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  Scadenze
                  {scadenzeMultiple.length > 0 && (
                    <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                      {scadenzeMultiple.length} {scadenzeMultiple.length === 1 ? "rata" : "rate"}
                    </Badge>
                  )}
                </h4>
              </div>

              {loadingScadenze ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : (
                <>
                  {scadenzeMultiple.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-2">Nessuna scadenza configurata</p>
                  )}

                  {scadenzeMultiple.length > 0 && (
                    <div className="space-y-2">
                      {scadenzeMultiple.map((scad, idx) => (
                        <div key={scad.id} className="bg-slate-50 p-3 rounded space-y-2">
                          {editingRataId === scad.id ? (
                            <div className="grid grid-cols-4 gap-2 items-end">
                              <div>
                                <Label className="text-xs">Data *</Label>
                                <DateInput
                                  value={editRataForm.data_scadenza}
                                  onChange={(e) => setEditRataForm({ ...editRataForm, data_scadenza: e.target.value })}
                                  className="h-9"
                                  data-testid={`edit-rata-date-${scad.id}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Importo *</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={editRataForm.importo}
                                  onChange={(e) => setEditRataForm({ ...editRataForm, importo: e.target.value })}
                                  className="h-9"
                                  data-testid={`edit-rata-importo-${scad.id}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">{t('common.notes')}</Label>
                                <Input
                                  value={editRataForm.note}
                                  onChange={(e) => setEditRataForm({ ...editRataForm, note: e.target.value })}
                                  className="h-9"
                                  data-testid={`edit-rata-note-${scad.id}`}
                                />
                              </div>
                              <div className="flex gap-1">
                                <Button size="sm" onClick={() => handleUpdateRata(scad.id)} className="h-9 flex-1" data-testid={`save-rata-${scad.id}`}>{t('common.save')}</Button>
                                <Button size="sm" variant="outline" onClick={() => setEditingRataId(null)} className="h-9" data-testid={`cancel-rata-${scad.id}`}>
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <Badge variant="outline" className="text-[10px]">#{idx + 1}</Badge>
                                <span className="font-mono text-sm font-medium">
                                  {new Date(scad.data_scadenza).toLocaleDateString('it-IT')}
                                </span>
                                <span className="font-mono text-sm">{formatCurrency(scad.importo)}</span>
                                {scad.note && <span className="text-xs text-muted-foreground italic">{scad.note}</span>}
                              </div>
                              <div className="flex gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => {
                                    const dateStr = scad.data_scadenza ? new Date(scad.data_scadenza).toISOString().split('T')[0] : "";
                                    setEditingRataId(scad.id);
                                    setEditRataForm({ data_scadenza: dateStr, importo: String(scad.importo || ""), note: scad.note || "" });
                                  }}
                                  title={t("invoices.editDueDate")}
                                  data-testid={`edit-rata-btn-${scad.id}`}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-destructive h-8 w-8 p-0"
                                  onClick={() => handleDeleteRata(scad.id)}
                                  title={t("invoices.deleteDueDate")}
                                  data-testid={`delete-rata-btn-${scad.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Riepilogo totale + Alert delta */}
                  {scadenzeMultiple.length > 0 && (() => {
                    const totScadenze = scadenzeMultiple.reduce((s, r) => s + (r.importo || 0), 0);
                    const totFattura = Math.abs(parseFloat(scadenzaDialog.invoice?.totale) || 0);
                    const delta = totFattura - totScadenze;
                    return (
                      <>
                        <div className="flex justify-between text-sm pt-3 border-t font-medium">
                          <span>Totale Scadenze:</span>
                          <span className="font-mono">
                            {formatCurrency(totScadenze)}
                          </span>
                        </div>
                        {Math.abs(delta) > 0.01 && (
                          <div className={`p-3 rounded-md text-sm flex items-center gap-2 ${delta > 0 ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-red-50 border border-red-200 text-red-800'}`}
                               data-testid="scadenze-delta-alert">
                            <AlertTriangle className="h-4 w-4 shrink-0" />
                            <div>
                              {delta > 0 ? (
                                <span>Mancano <strong className="font-mono">{formatCurrency(delta)}</strong> per coprire il totale fattura di {formatCurrency(totFattura)}</span>
                              ) : (
                                <span>Le scadenze superano il totale fattura di <strong className="font-mono">{formatCurrency(Math.abs(delta))}</strong></span>
                              )}
                            </div>
                          </div>
                        )}
                        {Math.abs(delta) <= 0.01 && (
                          <div className="p-2 rounded-md text-xs text-green-700 bg-green-50 border border-green-200 flex items-center gap-2">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Totale scadenze coincide con il totale fattura
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* Form nuova scadenza */}
                  <div className="pt-3 border-t">
                    <p className="text-xs text-muted-foreground mb-2">{t('invoices.addNewDueDate')}</p>
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <Label className="text-xs">Data *</Label>
                        <DateInput
                          value={nuovaRata.data_scadenza}
                          onChange={(e) => setNuovaRata({ ...nuovaRata, data_scadenza: e.target.value })}
                          className="h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Importo *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={nuovaRata.importo}
                          onChange={(e) => setNuovaRata({ ...nuovaRata, importo: e.target.value })}
                          placeholder="0.00"
                          className="h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{t("common.notes")}</Label>
                        <Input
                          value={nuovaRata.note}
                          onChange={(e) => setNuovaRata({ ...nuovaRata, note: e.target.value })}
                          placeholder={t("common.optional")}
                          className="h-9"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button size="sm" onClick={handleAddRata} className="w-full h-9">
                          + Aggiungi
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScadenzaDialog({ open: false, invoice: null })}>{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Invoice Dialog */}
      <Dialog open={newInvoiceDialog} onOpenChange={setNewInvoiceDialog}>
        <DialogContent className="max-w-lg" data-testid="new-invoice-dialog">
          <DialogHeader>
            <DialogTitle>{t("invoices.newInvoice")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo Documento *</Label>
                <Select 
                  value={newInvoiceForm.tipo_documento} 
                  onValueChange={(v) => setNewInvoiceForm({ ...newInvoiceForm, tipo_documento: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FV">FV - Fattura Vendita</SelectItem>
                    <SelectItem value="FA">FA - Fattura Acquisto</SelectItem>
                    <SelectItem value="FVE">FVE - Fattura Vendita Estera</SelectItem>
                    <SelectItem value="FAE">FAE - Fattura Acquisto Estera</SelectItem>
                    <SelectItem value="NCV">NCV - Nota Credito Vendita</SelectItem>
                    <SelectItem value="NCA">NCA - Nota Credito Acquisto</SelectItem>
                    <SelectItem value="NCVE">NCVE - Nota Credito Vendita Estera</SelectItem>
                    <SelectItem value="NCAE">NCAE - Nota Credito Acquisto Estera</SelectItem>
                    <SelectItem value="NDV">NDV - Nota Debito Vendita</SelectItem>
                    <SelectItem value="NDA">NDA - Nota Debito Acquisto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Data Documento *</Label>
                <InputWithError
                  type="text"
                  value={newInvoiceForm.data}
                  onChange={(e) => setNewInvoiceForm({ ...newInvoiceForm, data: e.target.value })}
                  onValidate={() => validateInvoiceField('data', newInvoiceForm.data)}
                  placeholder="GG-MM-AAAA"
                  error={invoiceErrors.data}
                  data-testid="input-data-documento"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Numero *</Label>
                <InputWithError
                  value={newInvoiceForm.numero}
                  onChange={(e) => setNewInvoiceForm({ ...newInvoiceForm, numero: e.target.value })}
                  onValidate={() => validateInvoiceField('numero', newInvoiceForm.numero)}
                  placeholder="Es: 001/2025"
                  error={invoiceErrors.numero}
                  data-testid="input-numero"
                />
              </div>
              <div className="space-y-2">
                <Label>Ragione Sociale *</Label>
                <InputWithError
                  value={newInvoiceForm.ragione_sociale}
                  onChange={(e) => setNewInvoiceForm({ ...newInvoiceForm, ragione_sociale: e.target.value })}
                  onValidate={() => validateInvoiceField('ragione_sociale', newInvoiceForm.ragione_sociale)}
                  placeholder="Es: Azienda SRL"
                  error={invoiceErrors.ragione_sociale}
                  data-testid="input-ragione-sociale"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('invoices.taxableAmount')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newInvoiceForm.imponibile}
                  onChange={(e) => updateNewInvoiceForm("imponibile", e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>IVA</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newInvoiceForm.imposta}
                  onChange={(e) => updateNewInvoiceForm("imposta", e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("common.total")}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newInvoiceForm.totale}
                  onChange={(e) => setNewInvoiceForm({ ...newInvoiceForm, totale: e.target.value })}
                  placeholder="0.00"
                  className="font-bold"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('invoices.dueDate')}</Label>
                <Input
                  type="text"
                  value={newInvoiceForm.data_scadenza}
                  onChange={(e) => setNewInvoiceForm({ ...newInvoiceForm, data_scadenza: e.target.value })}
                  placeholder="GG-MM-AAAA (opz.)"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('invoices.paymentMethod')}</Label>
                <SearchableSelect
                  options={[
                    { value: "none", label: "-- Nessuna --" },
                    ...paymentMethods.map(pm => ({ value: pm.nome, label: `${pm.codice} - ${pm.nome}` }))
                  ]}
                  value={newInvoiceForm.modalita_pagamento || "none"}
                  onValueChange={(v) => setNewInvoiceForm({ ...newInvoiceForm, modalita_pagamento: v === "none" ? "" : v })}
                  placeholder={t("common.selectPlaceholder")}
                  searchPlaceholder="Cerca modalità..."
                  emptyMessage="Nessuna modalità trovata"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewInvoiceDialog(false)} disabled={savingInvoice}>{t("common.cancel")}</Button>
            <Button onClick={handleCreateInvoice} data-testid="create-invoice-submit" disabled={savingInvoice}>
              {savingInvoice ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('dialogs.creating')}</> : 'Crea Fattura'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Dialog */}
      <Dialog open={editInvoiceDialog.open} onOpenChange={(open) => { if (!open) setEditInvoiceDialog({ open: false, invoice: null }); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("invoices.editInvoice")}</DialogTitle>
            <DialogDescription>Modifica i dati della fattura {editInvoiceDialog.invoice?.numero}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo Documento *</Label>
                <Select 
                  value={newInvoiceForm.tipo_documento} 
                  onValueChange={(v) => setNewInvoiceForm({ ...newInvoiceForm, tipo_documento: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FV">FV - Fattura Vendita</SelectItem>
                    <SelectItem value="FA">FA - Fattura Acquisto</SelectItem>
                    <SelectItem value="FVE">FVE - Fattura Vendita Estera</SelectItem>
                    <SelectItem value="FAE">FAE - Fattura Acquisto Estera</SelectItem>
                    <SelectItem value="NCV">NCV - Nota Credito Vendita</SelectItem>
                    <SelectItem value="NCA">NCA - Nota Credito Acquisto</SelectItem>
                    <SelectItem value="NCVE">NCVE - Nota Credito Vendita Estera</SelectItem>
                    <SelectItem value="NCAE">NCAE - Nota Credito Acquisto Estera</SelectItem>
                    <SelectItem value="NDV">NDV - Nota Debito Vendita</SelectItem>
                    <SelectItem value="NDA">NDA - Nota Debito Acquisto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Data Documento *</Label>
                <Input type="text" value={newInvoiceForm.data} onChange={(e) => setNewInvoiceForm({ ...newInvoiceForm, data: e.target.value })} placeholder="GG-MM-AAAA" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Numero *</Label>
                <Input value={newInvoiceForm.numero} onChange={(e) => setNewInvoiceForm({ ...newInvoiceForm, numero: e.target.value })} placeholder="Es: 001/2025" />
              </div>
              <div className="space-y-2">
                <Label>Ragione Sociale *</Label>
                <Input value={newInvoiceForm.ragione_sociale} onChange={(e) => setNewInvoiceForm({ ...newInvoiceForm, ragione_sociale: e.target.value })} placeholder="Es: Azienda SRL" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('invoices.taxableAmount')}</Label>
                <Input type="number" step="0.01" value={newInvoiceForm.imponibile} onChange={(e) => updateNewInvoiceForm("imponibile", e.target.value)} placeholder="0.00" />
              </div>
              <div className="space-y-2">
                <Label>IVA</Label>
                <Input type="number" step="0.01" value={newInvoiceForm.imposta} onChange={(e) => updateNewInvoiceForm("imposta", e.target.value)} placeholder="0.00" />
              </div>
              <div className="space-y-2">
                <Label>{t("common.total")}</Label>
                <Input type="number" step="0.01" value={newInvoiceForm.totale} onChange={(e) => setNewInvoiceForm({ ...newInvoiceForm, totale: e.target.value })} placeholder="0.00" className="font-bold" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditInvoiceDialog({ open: false, invoice: null })} disabled={savingInvoice}>{t("common.cancel")}</Button>
            <Button onClick={handleUpdateInvoice} disabled={savingInvoice}>
              {savingInvoice ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("common.saving")}</> : 'Salva Modifiche'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Delete Multiple Confirmation */}
      <Dialog open={deleteMultipleDialog} onOpenChange={setDeleteMultipleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare {selectedIds.size} fatture selezionate? 
              Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteMultipleDialog(false)} disabled={deletingBulk}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleDeleteMultiple} disabled={deletingBulk} data-testid="confirm-delete-multiple">
              {deletingBulk ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('common.deleting')}</> : `Elimina ${selectedIds.size} fatture`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Task Dialog */}
      <Dialog open={assignTaskDialog.open} onOpenChange={(open) => setAssignTaskDialog({ ...assignTaskDialog, open })}>
        <DialogContent data-testid="assign-task-dialog">
          <DialogHeader>
            <DialogTitle>{t("tasks.assignTask")}</DialogTitle>
            <DialogDescription>
              Delega l&apos;allocazione di questa fattura a un altro utente
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-muted p-3 rounded-md">
              <p className="text-sm text-muted-foreground">Fattura: {assignTaskDialog.invoice?.numero}</p>
              <p className="text-sm font-medium">{assignTaskDialog.invoice?.ragione_sociale || assignTaskDialog.invoice?.fornitore_cliente}</p>
              <p className="text-sm">Residuo: {formatCurrency(Math.abs(assignTaskDialog.invoice?.imponibile || 0) - (assignTaskDialog.invoice?.imponibile_allocato || 0))}</p>
            </div>
            
            <div className="space-y-2">
              <Label>Assegna a *</Label>
              <SearchableSelect
                options={users.map(u => ({ value: u.id, label: `${u.nome || u.email}${u.ruolo ? ` (${u.ruolo})` : ""}` }))}
                value={assignTaskForm.assigned_to}
                onValueChange={(v) => setAssignTaskForm({ ...assignTaskForm, assigned_to: v })}
                placeholder={t("dialogs.selectUser")}
                searchPlaceholder="Cerca utente..."
                testId="assign-task-user"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('invoices.allocationType')}</Label>
              <Select value={assignTaskForm.task_type} onValueChange={(v) => setAssignTaskForm({ ...assignTaskForm, task_type: v })}>
                <SelectTrigger data-testid="assign-task-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="appartamenti">{t('apartments.title')}</SelectItem>
                  <SelectItem value="automezzi">{t('vehicles.title')}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                L&apos;utente selezionerà l&apos;{assignTaskForm.task_type === 'appartamenti' ? 'appartamento' : 'automezzo'} a cui allocare il costo
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("common.amount")}</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={assignTaskForm.importo}
                  onChange={(e) => setAssignTaskForm({ ...assignTaskForm, importo: e.target.value })}
                  data-testid="assign-task-importo"
                />
              </div>
              <div className="space-y-2">
                <Label>Note (opzionale)</Label>
                <Input
                  value={assignTaskForm.note}
                  onChange={(e) => setAssignTaskForm({ ...assignTaskForm, note: e.target.value })}
                  placeholder="Istruzioni..."
                  data-testid="assign-task-note"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignTaskDialog({ open: false, invoice: null })} disabled={assigningTask}>{t('common.cancel')}</Button>
            <Button onClick={handleAssignTask} data-testid="assign-task-submit" disabled={assigningTask}>
              {assigningTask ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Assegnazione...</>
              ) : (
                <><UserPlus className="h-4 w-4 mr-2" /> Assegna Compito</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
